import { createMotion, advanceMotion, type CharacterMotion } from '@/gfx/motion';
import { drawRaster, WORLD_SCALE } from '@/engine/canvas';
import { drawBlast } from '@/gfx/effects';
import { INTERNAL_W, INTERNAL_H, UI_W, UI_H, type Screen } from '@/engine/canvas';
import { Input } from '@/engine/input';
import { TILE } from '@/gfx/tiles';
import { clamp, dist, facingFromVec, facingAngle, angleDiff, pointInPolygon, type Facing, type Seg } from '@/engine/math';
import { Rng } from '@/engine/rng';
import { Mansion, mansionDef } from '@/world/mansion';
import { type Floor, type DoorEnt, type PropEnt, type DecorEnt, roomAtPx, idx, markSolid, rebuildStatic, propFootprintFree } from '@/world/builder';
import { T_STAIRS, T_FLOOR, T_WALL, T_VOID, FLOOR_NAMES, type FloorId } from '@/world/types';
import { FRIENDS, INVENTORY_DEFAULT, type FriendDef } from './roster';
import { makeCharacter, makeMonster, type Character, type Monster, type GroundItem, type Noise, type Effect, type Projectile } from './entities';
import { moveBox, tileOf, tileCenter, boxFree, unstick, HW, HH } from './physics';
import { View, clampCamera } from './view';
import { drawDoorEnt, doorSprite, drawProp, propScreenRect, terrainCanvas, propImage, drawFurnished } from './render';
import { buildMonster, MON_W, MON_H, MON_FRAMES, monsterBob, type MonsterSprites } from '@/gfx/monster';
import { SPR_W, SPR_H, WALK_FRAMES, rotated, characterFrame, characterPortrait, type Expression } from '@/gfx/characters';
import { hasLos, visibility } from '@/gfx/lighting';
import { text, panel, bar, drawScaled } from './hud';
import { T, locationLabel } from './text';
import { ITEMS, itemIcon, type ItemKind } from './items';
import { updateMonster, spawnMonster, monsterEyesVisible, monsterWitnesses } from './monster';
import { updateFollowers, switchLeader, switchLeaderTo } from './followers';
import { generateRun, type RunConfig, type Prison } from './run';
import { Audio } from './audio';
import { propSprite, propLightPos } from '@/world/props';
import { drawScreens, type Phase, type Overlay } from './screens';
import { saveGame, loadInto, clearSave } from './save';

import { overlaps, coverOpacity, visibleFootprint, doorLightSegments, withoutEmitter, type Rect } from '@/gfx/occlusion';
import { silhouette } from '@/gfx/pixel';
import { C } from '@/gfx/palette';
import { animateFurniture } from '@/gfx/furniture';
import { dropSound, footstepSurface } from './sound';
import { passageOpacity } from './passages';
import { isPaper, rememberPaper, slotsNeeded, type JournalEntry } from './journal';
import { openMap, changeMapFloor, clickMapFloor } from './map';
import { dropPoint } from './item-placement';
import { handleTap } from './touch';
import { titleFlashIndex } from './title';
import { drainFlashlight } from './flashlight';

export const SPEED = { sneak: 30, walk: 58, run: 96 };
export const NOISE_R = { sneak: 22, walk: 70, run: 150 };

export interface Interaction {
  label: string;
  run: () => void;
  x: number;
  y: number;
}

export class Game {
  seed: number;
  rng: Rng;
  mansion: Mansion;
  chars = new Map<string, Character>();
  monster: Monster = makeMonster();
  monSprites: MonsterSprites = buildMonster();
  items: GroundItem[] = [];
  noises: Noise[] = [];
  effects: Effect[] = [];
  projectiles: Projectile[] = [];
  leaderId = 'haneul';
  view = new View();
  time = 0;
  input: Input;
  audio = new Audio();
  flags: Record<string, boolean | number | string> = {};
  message: { text: string; t: number } | null = null;
  interaction: Interaction | null = null;
  camX = 0;
  camY = 0;
  shake = 0;
  debugBright = false;
  debugOverview = false;
  cfg!: RunConfig;
  entered: string[] = [];
  runs = 0;
  captures = 0;
  phase: Phase = 'title';
  overlay: Overlay = 'none';
  phaseTimer = 0;
  menuIndex = 0;
  selectIndex = 0;
  noteText = '';
  journal: JournalEntry[] = [];
  journalIndex = 0;
  safeInput = '';
  safeProp: PropEnt | null = null;
  selectedSlot = 0;
  lightning = 0;
  lightningTimer = 30;
  thunderDelay = 0;
  heart = 0;
  autosave = 0;
  pushTimer = 0;
  losVis = { pts: new Float32Array(8192), n: 0 };
  bagTarget: GroundItem | null = null;
  bagSide = 0;
  bagIndex = 0;
  bagNotice: string | null = null;
  introText = '';
  endStats = { time: 0, runs: 0 };
  hasSave = false;
  private titleFlash = -1;
  mapFloor: FloorId = 'ground';
  private propOpacity = new WeakMap<PropEnt, number>();
  private doorOpacity = new WeakMap<DoorEnt, number>();
  private coveredLeader = false;
  private visibleProps = new Set<PropEnt>();
  private coverRects: Rect[] = [];
  private frameDt = 1 / 60;
  private lastRender = 0;
  private renderAlpha = 1;
  private characterMotions = new WeakMap<Character, CharacterMotion & { x: number; y: number; floor: FloorId }>();
  private previousPositions = new WeakMap<object, { x: number; y: number; floor: FloorId }>();
  private ghostCache = new WeakMap<HTMLCanvasElement, HTMLCanvasElement>();

  constructor(public screen: Screen, seed: number) {
    this.seed = seed;
    this.rng = new Rng(seed);
    this.mansion = new Mansion(mansionDef(), seed);
    this.input = new Input(screen.display, screen.toInternal);
    this.input.onGesture = () => { this.audio.init(); this.audio.resume(); };
    for (const f of FRIENDS) this.chars.set(f.id, makeCharacter(f));
    this.cfg = generateRun(this);
    this.hasSave = hasSaveFor();
  }

  // ---------------- run setup ----------------
  startNewRun(): void {
    clearSave();
    // 지우 is already captured in the first prison
    const jiwoo = this.chars.get('jiwoo')!;
    this.imprisonChar(jiwoo, this.cfg.prisons[0]);
    this.entered = ['jiwoo'];
    spawnMonster(this);
    this.phase = 'select';
    this.selectIndex = 0;
  }
  continueRun(): void {
    if (!loadInto(this)) { this.startNewRun(); return; }
    const available = [...this.chars.values()].filter(c => ['free', 'hidden', 'groggy'].includes(c.state));
    if (available.length) {
      if (!available.some(c => c.id === this.leaderId)) { this.leaderId = available[0].id; this.selectedSlot = 0; }
      this.phase = 'play';
    } else this.phase = this.selectable().length ? 'select' : 'gameover';
    this.phaseTimer = 0; this.selectIndex = 0; this.overlay = 'none';
    this.say('이어서 시작한다.', 2);
  }

  enterFriend(id: string): void {
    const c = this.chars.get(id)!;
    const ground = this.mansion.get('ground');
    const front = ground.doors.find((d) => d.kind === 'exit')!;
    c.state = 'free';
    c.mode = 'follow';
    c.floor = 'ground';
    c.x = (front.x + front.w / 2) * TILE;
    c.y = front.y * TILE - 12;
    c.facing = 'up';
    c.battery = 1;
    c.flashOn = false;
    c.inventory = [];
    this.leaderId = id;
    if (!this.entered.includes(id)) this.entered.push(id);
    this.runs++;
    // the monster gets a little bolder each time
    this.monster.speedMul = 1 + (this.runs - 1) * 0.03;
    this.introText = `${c.def.name}${josa(c.def.name, '이', '가')} 저택에 들어섰다.\n등 뒤에서 정문이 무겁게 잠겼다.\n\n${this.capturedNames().length ? `${this.capturedNames().join(', ')}${josa(this.capturedNames().slice(-1)[0], '이', '가')} 어딘가에 갇혀 있다.` : ''}`;
    this.phase = 'intro';
    this.phaseTimer = 0;
    this.overlay = 'none';
    this.selectedSlot = 0;
  }

  capturedNames(): string[] {
    return [...this.chars.values()].filter((c) => c.state === 'captured' || c.state === 'carried').map((c) => c.def.name);
  }

  get leader(): Character {
    return this.chars.get(this.leaderId)!;
  }
  floorOf(c: { floor: FloorId }): Floor {
    return this.mansion.get(c.floor);
  }

  // ---------------- update ----------------
  update(dt: number): void {
    const inp = this.input;
    inp.beginFrame();
    if (inp.anyKeyPressed || inp.mousePressed) { this.audio.init(); this.audio.resume(); }
    if (inp.tapped) handleTap(this, inp.tapX * 2, inp.tapY * 2);
    if (this.phase !== 'play') {
      this.phaseTimer += dt;
      this.updateScreens(dt);
      inp.endFrame();
      return;
    }
    if (this.overlay !== 'none') {
      this.updateOverlay(dt);
      inp.endFrame();
      return;
    }
    if (inp.pressed('Escape')) { this.overlay = 'pause'; this.menuIndex = 0; inp.endFrame(); return; }
    if (inp.pressed('KeyM')) { openMap(this); inp.endFrame(); return; }
    if (inp.pressed('KeyJ')) { this.overlay = 'journal'; inp.endFrame(); return; }
    if (inp.pressed('F1')) this.debugBright = !this.debugBright;
    if (inp.pressed('F2')) this.debugOverview = !this.debugOverview;
    for (const c of [...this.chars.values(), this.monster]) this.previousPositions.set(c, { x: c.x, y: c.y, floor: c.floor });
    this.time += dt;
    this.updateLeader(dt);
    updateFollowers(this, dt);
    updateMonster(this, dt);
    this.updateProjectiles(dt);
    this.updateDoors(dt);
    this.updateEffects(dt);
    this.updateAmbience(dt);
    if (this.message && (this.message.t -= dt) <= 0) this.message = null;
    if (this.shake > 0) this.shake = Math.max(0, this.shake - dt * 3);
    if (this.pushTimer > 0) this.pushTimer -= dt;
    this.autosave += dt;
    if (this.autosave > 20) { this.autosave = 0; saveGame(this); }
    inp.endFrame();
  }

  say(text: string, t = 3): void {
    this.message = { text, t };
  }
  emitNoise(n: Omit<Noise, 't'>): void {
    this.noises.push({ ...n, t: 0.5 });
    if (n.kind !== 'step' && n.floor === this.leader.floor) this.effects.push({ kind: 'ring', floor: n.floor, x: n.x, y: n.y, t: 0, dur: 0.6, r: n.r });
  }
  fx(e: Omit<Effect, 't'>): void {
    this.effects.push({ ...e, t: 0 });
  }

  // ---------------- doors ----------------
  doorRuleFor(c: Character): (d: DoorEnt) => boolean {
    return (d) => {
      if (d.open) return true;
      if (d.locked || (d.kind === 'secret' && !d.revealed)) return false;
      if (c.state !== 'free') return false;
      this.openDoor(d, c);
      return true;
    };
  }
  openDoor(d: DoorEnt, by: Character | null): void {
    if (d.open) return;
    d.open = true;
    d.anim = 0.4;
    if (by) {
      this.emitNoise({ floor: by.floor, x: (d.x + d.w / 2) * TILE, y: (d.y + 0.5) * TILE, r: 90, kind: 'door', from: by.id });
      if (by.floor === this.leader.floor) this.audio.door();
    }
  }
  closeDoor(d: DoorEnt, by: Character): boolean {
    if (!d.open) return false;
    const x0 = d.x * TILE, y0 = d.y * TILE;
    const x1 = x0 + (d.orient === 'h' ? d.w : 1) * TILE;
    const y1 = y0 + (d.orient === 'v' ? d.w : 1) * TILE;
    const overlaps = (body: { x: number; y: number }, hw: number, hh: number) =>
      body.x + hw - 1 >= x0 && body.x - hw < x1 && body.y + hh - 1 >= y0 && body.y - hh < y1;
    const occupied = [...this.chars.values()].some(c => c.floor === by.floor &&
      (c.state === 'free' || c.state === 'groggy' || c.state === 'captured') && overlaps(c, HW, HH));
    const m = this.monster;
    if (occupied || m.floor === by.floor && m.transit?.phase !== 'cross' && overlaps(m, 6, 4)) {
      this.say('문간이 막혀 있어 닫을 수 없다.', 2);
      return false;
    }
    d.open = false;
    this.emitNoise({ floor: by.floor, x: (x0 + x1) / 2, y: (y0 + y1) / 2, r: 60, kind: 'door', from: by.id });
    this.audio.door();
    return true;
  }
  trigger(flag: string): void {
    this.flags[flag] = true;
    for (const f of this.mansion.floors.values()) {
      let changed = false;
      for (const d of f.doors) {
        if (d.trigger !== flag) continue;
        d.revealed = true; d.locked = false; d.open = true; d.anim = 0.5;
        changed = true;
      }
      if (changed) f.staticDirty = true;
    }
  }

  // ---------------- leader control ----------------
  updateLeader(dt: number): void {
    const c = this.leader;
    const inp = this.input;
    const f = this.floorOf(c);
    if (c.bubble && (c.bubble.t -= dt) <= 0) c.bubble = null;
    if (inp.pressed('Tab')) { if (switchLeader(this)) { this.audio.ui(); this.say(`${this.leader.def.name}${josa(this.leader.def.name, '을', '를')} 조종한다.`, 1.5); } return; }
    if (inp.pressed('Digit1')) this.command('follow');
    if (inp.pressed('Digit2')) this.command('wait');
    if (inp.pressed('Digit3')) this.command('hide');
    if (inp.pressed('KeyG')) this.dropSelected();
    if (inp.pressed('KeyQ')) this.selectSlot(-1);
    if (inp.pressed('KeyR') || inp.wheel > 0) this.selectSlot(1);
    if (inp.wheel < 0) this.selectSlot(-1);
    if (c.state === 'groggy') {
      c.groggy -= dt;
      c.moving = false;
      if (c.groggy <= 0) c.state = 'free';
      this.interaction = null;
      return;
    }
    if (c.state !== 'free' && c.state !== 'hidden') { this.interaction = null; return; }
    if (c.state === 'free') unstick(f, c);
    let mx = 0, my = 0;
    if (inp.anyDown('KeyA', 'ArrowLeft')) mx -= 1;
    if (inp.anyDown('KeyD', 'ArrowRight')) mx += 1;
    if (inp.anyDown('KeyW', 'ArrowUp')) my -= 1;
    if (inp.anyDown('KeyS', 'ArrowDown')) my += 1;
    const run = inp.anyDown('ShiftLeft', 'ShiftRight');
    const sneak = inp.anyDown('ControlLeft', 'ControlRight', 'KeyC');
    c.gait = sneak ? 'sneak' : run ? 'run' : 'walk';
    let speed = SPEED[c.gait];
    if (c.gait === 'run' && c.def.trait.runMul) speed *= c.def.trait.runMul;
    if (mx && my) { mx *= Math.SQRT1_2; my *= Math.SQRT1_2; }
    const stick = !mx && !my && (inp.moveX !== 0 || inp.moveY !== 0);
    if (stick) { mx = inp.moveX; my = inp.moveY; }
    c.moving = (mx !== 0 || my !== 0) && c.state === 'free' && this.pushTimer <= 0;
    if (c.moving) {
      const oldX = c.x, oldY = c.y;
      moveBox(f, c, mx * speed * dt, my * speed * dt, this.doorRuleFor(c));
      c.moving = dist(oldX, oldY, c.x, c.y) > 0.001;
      if (c.moving) c.anim += dt * (c.gait === 'run' ? 12 : c.gait === 'sneak' ? 5 : 8);
      c.facing = facingFromVec(mx, my, c.facing);
      if (c.moving) c.stepTimer -= dt;
      if (c.moving && c.stepTimer <= 0) {
        c.stepTimer = c.gait === 'run' ? 0.25 : c.gait === 'sneak' ? 0.6 : 0.4;
        let r = NOISE_R[c.gait];
        r *= footstepSurface(roomAtPx(f, c.x, c.y)?.floor ?? 'wood');
        if (c.def.trait.noiseMul) r *= c.def.trait.noiseMul;
        this.emitNoise({ floor: c.floor, x: c.x, y: c.y, r, kind: 'step', from: c.id });
        c.noiseLevel = r;
        this.audio.step(c.gait);
      }
    }
    if (!c.moving) {
      c.anim = 0;
      c.noiseLevel = Math.max(0, c.noiseLevel - dt * 200);
    }
    const sx = c.x - this.camX, sy = c.y - 10 - this.camY;
    // Aim priority: aim stick, then mouse, then the direction of travel.
    const ang = inp.aimAngle !== null ? inp.aimAngle
      : inp.mouseKnown ? Math.atan2(inp.mouseY - sy, inp.mouseX - sx)
      : stick ? Math.atan2(my, mx)
      : mx || my ? facingAngle(c.facing) : c.flashAngle;
    c.flashAngle += angleDiff(c.flashAngle, ang) * (1 - Math.exp(-dt * 22));
    if (!c.moving && c.flashOn) c.facing = facingFromAngleSafe(ang, c.facing);
    if (inp.pressed('KeyF') && c.state === 'free') {
      c.flashOn = !c.flashOn;
      this.audio.ui();
      if (c.flashOn && c.battery <= 0) { c.flashOn = false; this.say('배터리가 없다.'); this.audio.deny(); }
    }
    if (drainFlashlight(c, dt)) this.say('손전등이 꺼졌다...');
    if (c.state === 'free') this.checkStairs(c);
    this.interaction = this.findInteraction(c);
    if (inp.pressed('KeyE') && this.interaction) this.interaction.run();
    if (inp.pressed('Space') || inp.mousePressed) this.useSelected();
  }

  command(mode: 'follow' | 'wait' | 'hide'): void {
    const l = this.leader;
    let n = 0;
    for (const c of this.chars.values()) {
      if (c.id === l.id || (c.state !== 'free' && c.state !== 'hidden')) continue;
      if (c.floor !== l.floor || dist(c.x, c.y, l.x, l.y) > 260) continue;
      c.mode = mode;
      c.path = null;
      c.bubble = { text: mode === 'follow' ? '갈게!' : mode === 'wait' ? '기다릴게' : '숨을게', t: 1.5 };
      n++;
    }
    this.audio.ui();
    if (n) this.say(`${mode === 'follow' ? '따라와!' : mode === 'wait' ? '여기서 기다려.' : '근처에 숨어!'} (${n}명)`, 2);
    else this.say('같은 층 가까이에 신호를 받을 친구가 없다.', 2.5);
  }

  selectSlot(d: number): void {
    const n = this.slotCount(this.leader);
    this.selectedSlot = (this.selectedSlot + d + n) % n;
    this.audio.ui();
  }
  slotCount(c: Character): number {
    return c.def.trait.inventory ?? INVENTORY_DEFAULT;
  }

  checkStairs(c: Character): void {
    const f = this.floorOf(c);
    const tx = tileOf(c.x), ty = tileOf(c.y);
    const i = idx(f, tx, ty);
    if (f.tiles[i] !== T_STAIRS) return;
    const s = f.stairsAt.get(i)!;
    if (tx === s.far.x && ty === s.far.y) {
      if (!this.mansion.floors.has(s.def.toFloor)) { this.say('아직 갈 수 없다.'); c.x = tileCenter(s.entry.x); c.y = tileCenter(s.entry.y); return; }
      this.travel(c, s.def.toFloor, s.def.to);
      this.say(`${FLOOR_NAMES[s.def.toFloor]}${josa(FLOOR_NAMES[s.def.toFloor], '으로', '로')} ${s.def.dir === 'up' ? '올라간다' : '내려간다'}.`, 1.5);
      saveGame(this);
    }
  }
  travel(c: Character, toFloor: FloorId, stairsId: string): void {
    const tf = this.mansion.get(toFloor);
    const target = tf.stairs.find((s) => s.def.id === stairsId);
    if (!target) throw new Error('missing stairs ' + stairsId);
    if (monsterWitnesses(this, c)) {
      this.monster.lastSeen = { floor: toFloor, x: tileCenter(target.entry.x), y: tileCenter(target.entry.y) };
      this.monster.path = null;
    }
    c.floor = toFloor;
    c.x = tileCenter(target.entry.x);
    c.y = tileCenter(target.entry.y);
    c.facing = facingFromVec(target.entry.x - target.far.x, target.entry.y - target.far.y, c.facing);
    c.path = null;
  }

  // ---------------- interactions ----------------
  findInteraction(c: Character): Interaction | null {
    if (c.state === 'hidden') return { label: '나온다', x: c.x, y: c.y, run: () => this.unhide(c) };
    if (c.state !== 'free') return null;
    const f = this.floorOf(c);
    const fa = facingAngle(c.facing);
    const fx = c.x + Math.cos(fa) * 10, fy = c.y + Math.sin(fa) * 10;
    const reach = this.reachSegs(f);
    let best: Interaction | null = null;
    let bestD = 1e9;
    const consider = (x: number, y: number, label: string, run: () => void, range = 24) => {
      const d = Math.min(dist(c.x, c.y, x, y), dist(fx, fy, x, y) + 4);
      if (d >= range || d >= bestD) return;
      // no reaching through a wall or a shut door
      if (!hasLos(c.x, this.eyeY(c), x, y, reach)) return;
      bestD = d;
      best = { label, run, x, y };
    };
    for (const it of this.items) {
      if (it.floor !== c.floor) continue;
      if (it.bag) {
        const name = `${this.chars.get(it.bag)?.def.name}의 가방`;
        consider(it.x, it.y, `${name}${josa(name, '을', '를')} 연다`, () => this.pickup(c, it), 20);
        continue;
      }
      const name = ITEMS[it.kind].name + (it.count > 1 ? ` x${it.count}` : '');
      const full = !isPaper(it.kind) && c.inventory.length >= this.slotCount(c) && !c.inventory.some((i) => i.kind === it.kind && ITEMS[it.kind].stack);
      const held = c.inventory[this.selectedSlot];
      const label = full && held ? `${name}${josa(name, '을', '를')} ${ITEMS[held.kind].name}${josa(ITEMS[held.kind].name, '과', '와')} 바꾼다` : `${name}${josa(name, '을', '를')} 줍는다`;
      consider(it.x, it.y, label, () => this.pickup(c, it), 20);
    }
    for (const p of f.props) {
      const cx = (p.x + p.kind.fw / 2) * TILE, cy = (p.y + p.kind.fh / 2) * TILE;
      const reach = Math.max(p.kind.fw, p.kind.fh) * TILE * 0.5 + 12;
      if (p.kind.hide && p.occupants.length < p.kind.hide.cap) consider(cx, cy, p.kind.hide.verb, () => this.hide(c, p), reach);
      if (p.kind.search && !p.searched) consider(cx, cy, `${p.kind.name}${josa(p.kind.name, '을', '를')} 뒤진다`, () => this.search(c, p), reach);
      if (p.kind.push) consider(cx, cy, `${p.kind.name}${josa(p.kind.name, '을', '를')} 민다`, () => this.push(c, p), reach);
      else if (p.kind.interact && !(p.kind.interact === 'safe' && p.data.opened)) consider(cx, cy, `${p.kind.name}${josa(p.kind.name, '을', '를')} 살펴본다`, () => this.interactProp(c, p), reach);
    }
    for (const d of f.decor) {
      if (!['fusebox', 'painting', 'paintingBig', 'poster', 'hook', 'plaque', 'crawlspace'].includes(d.kind)) continue;
      const cx = (d.x + (d.kind === 'crawlspace' ? 1 : 0.5)) * TILE, cy = (d.y + 1.2) * TILE;
      const label = d.kind === 'crawlspace' ? '긁힌 철망을 살펴본다' : d.kind === 'fusebox' ? '두꺼비집을 살펴본다' : d.kind === 'poster' ? '벽의 종이를 읽는다' : d.kind === 'plaque' ? '명판을 읽는다' : d.kind === 'hook' ? '걸이를 살펴본다' : '그림을 살펴본다';
      consider(cx, cy, label, () => this.interactDecor(c, d), 18);
    }
    for (const d of f.doors) {
      if (d.kind === 'secret' && !d.revealed) continue;
      const cx = (d.x + d.w / 2) * TILE, cy = (d.y + 0.5) * TILE;
      if (d.locked) {
        const label = d.kind === 'exit' ? '정문' : d.label ?? '잠긴 문';
        consider(cx, cy, `${label} (잠김)`, () => this.tryUnlock(c, d), 22);
      } else if (!d.open && d.kind !== 'open' && d.kind !== 'arch' && d.kind !== 'exit') {
        consider(cx, cy, '문을 연다', () => this.openDoor(d, c), 22);
      } else if (d.open && d.kind !== 'open' && d.kind !== 'arch' && d.kind !== 'exit') {
        consider(cx, cy, '문을 닫는다', () => { this.closeDoor(d, c); }, 18);
      } else if (d.kind === 'exit' && !d.locked) {
        consider(cx, cy, '정문으로 탈출한다', () => this.tryEscape(c), 22);
      }
    }
    return best;
  }

  hide(c: Character, p: PropEnt, announce = true): void {
    if (p.occupants.length >= (p.kind.hide?.cap ?? 0)) return;
    const witnessed = monsterWitnesses(this, c);
    if (witnessed) this.monster.witnessedHide = p.id;
    c.state = 'hidden';
    c.hideProp = p.id;
    c.flashOn = false;
    c.moving = false;
    c.path = null;
    p.occupants.push(c.id);
    // remember the spot to step back out onto, then tuck into the furniture
    c.hideReturn = { x: c.x, y: c.y };
    c.x = (p.x + p.kind.fw / 2) * TILE;
    c.y = (p.y + p.kind.fh) * TILE - 4;
    if (announce) this.say(witnessed ? '숨는 모습을 들켰다!' : `${p.kind.hide!.label}에 숨었다.`, 2);
  }
  unhide(c: Character): void {
    const f = this.floorOf(c);
    const p = f.props.find((p) => p.id === c.hideProp);
    if (p) p.occupants = p.occupants.filter((id) => id !== c.id);
    c.state = 'free';
    c.hideProp = null;
    c.path = null;
    const free = (x: number, y: number) => boxFree(f, x, y, (d) => d.open);
    // step back out where we came from, otherwise onto the nearest clear tile by the furniture
    const back = c.hideReturn;
    c.hideReturn = null;
    if (back && free(back.x, back.y)) { c.x = back.x; c.y = back.y; return; }
    if (!p) return;
    const cx = (p.x + p.kind.fw / 2) * TILE;
    const south = (p.y + p.kind.fh) * TILE;
    const cands: Array<[number, number]> = [
      [cx, south + 8], [cx, south + 20],
      [p.x * TILE - 6, south - 8], [(p.x + p.kind.fw) * TILE + 6, south - 8],
      [cx, p.y * TILE - 8],
    ];
    // never step out into a different room: that would mean stepping through a wall
    for (const [x, y] of cands) if (free(x, y) && roomAtPx(f, x, y)?.id === p.room) { c.x = x; c.y = y; return; }
    unstick(f, c);
  }
  search(c: Character, p: PropEnt): void {
    p.searched = true;
    this.emitNoise({ floor: c.floor, x: c.x, y: c.y, r: 50, kind: 'search', from: c.id });
    const roll = this.rng.next();
    let kind: ItemKind | null = null;
    if (roll < 0.32) kind = 'battery';
    else if (roll < 0.5) kind = 'bottle';
    else if (roll < 0.6) kind = 'firecracker';
    if (!kind) { this.say('아무것도 없다.', 2); return; }
    const gi: GroundItem = { id: `loot:${p.id}`, kind, floor: c.floor, ...dropPoint(this.floorOf(c), c), count: 1 };
    this.items.push(gi);
    this.say(`${ITEMS[kind].name}${josa(ITEMS[kind].name, '을', '를')} 찾았다!`, 2);
    this.audio.pickup();
  }
  push(c: Character, p: PropEnt): void {
    if (this.pushTimer > 0) return;
    const f = this.floorOf(c);
    // push away from the character along x (the wall is behind it)
    const cx = (p.x + p.kind.fw / 2) * TILE;
    const dir = c.x <= cx ? 1 : -1;
    const nx = p.x + dir;
    const room = f.roomById.get(p.room)!;
    markSolid(f, p, false);
    const blockedByChar = [...this.chars.values()].some((o) => o.floor === c.floor && (o.state === 'free' || o.state === 'groggy')
      && tileOf(o.x) >= nx && tileOf(o.x) < nx + p.kind.fw && tileOf(o.y) >= p.y && tileOf(o.y) < p.y + p.kind.fh);
    const ok = !blockedByChar && propFootprintFree(f, room, p.kind, nx, p.y, false, p);
    if (!ok) { markSolid(f, p, true); this.say('더는 밀리지 않는다.', 2); this.audio.deny(); return; }
    p.data.originX ??= p.x;
    p.x = nx;
    p.pushed = Math.abs(p.x - Number(p.data.originX));
    markSolid(f, p, true);
    rebuildStatic(f);
    this.pushTimer = 0.9 / (c.def.trait.pushMul ?? 1);
    this.emitNoise({ floor: c.floor, x: cx, y: p.y * TILE, r: 140, kind: 'impact', from: c.id });
    this.audio.thud();
    this.fx({ kind: 'dust', floor: c.floor, x: cx, y: (p.y + 1) * TILE, dur: 0.5 });
    const trig = p.data.trigger as string | undefined;
    const need = (p.data.dist as number | undefined) ?? 1;
    if (trig && p.pushed >= need && !this.flags[trig]) {
      this.trigger(trig);
      this.say('옷장 뒤에서 숨겨진 문이 드러났다!', 4);
      this.audio.unlock();
    } else this.say('끙... 조금 밀렸다.', 1.5);
  }
  interactProp(c: Character, p: PropEnt): void {
    switch (p.kind.interact) {
      case 'lever': {
        if (p.data.pulled) { this.say('이미 건드린 책장이다.', 2); return; }
        p.data.pulled = true;
        if (p.data.index === this.cfg.leverIndex) {
          this.trigger('lever_library');
          this.say('책장이 스르르 밀려나며 숨겨진 문이 나타났다!', 4);
          this.audio.unlock();
        } else {
          this.say('책이 우수수 쏟아졌다! 소리가 크게 울린다...', 3);
          this.emitNoise({ floor: c.floor, x: c.x, y: c.y, r: 190, kind: 'rattle', from: c.id });
          this.audio.thud();
        }
        return;
      }
      case 'safe': {
        if (p.data.opened) { this.say('이미 열린 금고다.', 2); return; }
        this.safeProp = p;
        this.safeInput = '';
        this.overlay = 'safe';
        return;
      }
      case 'piano':
      case 'organ': {
        this.say(p.kind.interact === 'piano' ? '피아노 소리가 저택에 울려 퍼진다...' : '오르간이 낮게 울린다...', 3);
        this.emitNoise({ floor: c.floor, x: c.x, y: c.y, r: 260, kind: 'rattle', from: c.id });
        this.audio.jingle(false);
        return;
      }
      case 'clock': this.say('시계는 3시 7분에 멈춰 있다.', 2.5); return;
      case 'telescope': this.say('망원경 너머로 붉은 달이 보인다. 저택 밖은 안개뿐이다.', 3); return;
      case 'mirror': this.say('거울 속의 내가 한 박자 늦게 움직이는 것 같다...', 3); return;
      case 'toybox': this.search(c, p); return;
      case 'well': this.say('깊다. 저 아래에서 물 떨어지는 소리가 난다.', 2.5); return;
      case 'nest': this.say('역한 냄새. 뼈와 헝겊, 낡은 옷가지가 뒤엉켜 있다.', 3); return;
      case 'bedNote': this.say('침대 밑은 어둡고 먼지투성이다.', 2); return;
      default: this.say(`${p.kind.name}.`, 1.5);
    }
  }
  interactDecor(c: Character, d: DecorEnt): void {
    if (d.kind === 'crawlspace') {
      this.say('벽 안 통로다. 긁는 소리가 들리면 철망에서 떨어져야겠다.', 4);
      return;
    }
    if (d.kind === 'fusebox') {
      if (this.flags.power) { this.say('전기가 들어와 있다. 웅웅거리는 소리가 난다.', 2); return; }
      const fuse = c.inventory.find((it) => it.kind === 'fuse');
      if (!fuse) { this.say('퓨즈가 빠져 있다. 퓨즈를 끼워야 전기가 들어온다.', 3); this.audio.deny(); return; }
      this.removeItem(c, 'fuse', 1);
      this.trigger('power');
      this.say('전기가 들어왔다! 저택 어딘가에서 철문이 열리는 소리가 난다.', 4);
      this.audio.power();
      return;
    }
    if (d.id.startsWith('gal-p')) {
      const digit = d.data.digit as number | undefined;
      if (digit === undefined) { this.say('오래된 초상화다.', 3.5); return; }
      const slot = d.data.slot as number | undefined;
      const position = slot === undefined ? '초상화 액자' : `왼쪽에서 ${slot + 1}번째 액자`;
      const title = slot === undefined ? '초상화 액자' : `회랑 ${slot + 1}번째 액자`;
      rememberPaper(this, 'note', `[${title}]\n2층 초상화 회랑의 ${position}.\n아래에 작은 숫자 「${digit}」이 새겨져 있다.`);
      this.say(`초상화 액자 아래에 작은 숫자 「${digit}」이 새겨져 있다. [J 수첩]`, 3.5);
      return;
    }
    if (d.kind === 'poster') {
      const f = this.floorOf(c);
      const room = roomAtPx(f, c.x, c.y);
      if (room?.id === 'boilerroom') {
        const fr = this.mansion.get('basement').roomById.get(this.cfg.fuseRoom);
        const body = `「퓨즈 교체 예정. 새 퓨즈는 ${fr?.name ?? '창고'}에 두었음 - 관리인」`;
        rememberPaper(this, 'note', `[보일러실 퓨즈 메모]\n${body}`);
        this.say(`${body} [J 수첩]`, 4);
      } else this.say('빛바랜 종이. "밤에는 불을 켜지 마시오."', 3);
      return;
    }
    if (d.kind === 'plaque') { this.say('「이 집은 1887년에 지어졌다」', 2.5); return; }
    if (d.kind === 'hook') { this.say('빈 열쇠 걸이다.', 2); return; }
    const scenes = ['안개 낀 들판 그림이다.', '누군가의 초상화. 눈이 이쪽을 보는 것 같다.', '붉은 방 그림. 왠지 불길하다.', '달 없는 밤 풍경.', '아이의 초상화. 미소가 어색하다.'];
    this.say(scenes[d.variant % scenes.length], 2.5);
  }
  tryUnlock(c: Character, d: DoorEnt): void {
    if (d.kind === 'bolt') {
      d.locked = false; d.open = true; d.anim = 0.4;
      this.audio.unlock();
      this.say('빗장을 풀었다.', 2);
      this.rescueIn(d);
      return;
    }
    if (d.kind === 'exit') {
      if (c.inventory.some((it) => it.kind === 'key_master')) { d.locked = false; this.audio.unlock(); this.say('커다란 열쇠가 자물쇠에 맞았다! 정문이 열린다.', 3); this.tryEscape(c); }
      else { this.say('정문은 잠겨 있다. 커다란 열쇠 구멍이 보인다.', 3); this.audio.deny(); }
      return;
    }
    if (d.key && c.inventory.some((it) => it.kind === d.key)) {
      d.locked = false; d.open = true; d.anim = 0.4;
      this.audio.unlock();
      this.say(`${ITEMS[d.key as ItemKind].name}로 문을 열었다.`, 2.5);
      return;
    }
    if (d.kind === 'boarded') {
      if (c.inventory.some((it) => it.kind === 'crowbar')) {
        d.locked = false; d.open = true; d.anim = 0.4;
        this.say('판자를 뜯어냈다! 요란한 소리가 났다.', 3);
        this.emitNoise({ floor: c.floor, x: c.x, y: c.y, r: 200, kind: 'impact', from: c.id });
        this.audio.thud();
      } else { this.say('판자로 단단히 막혀 있다. 뜯어낼 도구가 필요하다.', 3); this.audio.deny(); }
      return;
    }
    if (d.kind === 'gate') { this.say('전기 철문이다. 전기가 들어와야 열린다.', 3); this.audio.deny(); return; }
    if (d.kind === 'cell') { this.say('녹슨 창살문. 열리지 않는다.', 2.5); this.audio.deny(); return; }
    this.say(d.key ? `잠겨 있다. ${ITEMS[d.key as ItemKind].name}가 필요하다.` : '잠겨 있다.', 2.5);
    this.audio.deny();
  }
  rescueIn(d: DoorEnt): void {
    const names: string[] = [];
    for (const c of this.chars.values()) {
      if (c.state !== 'captured') continue;
      if (!d.rooms.includes(c.prisonRoom ?? '')) continue;
      c.state = 'free';
      c.mode = 'follow';
      c.prisonRoom = null;
      c.path = null;
      c.bubble = { text: '고마워!', t: 2.5 };
      names.push(c.def.name);
    }
    if (names.length) {
      this.say(`${names.join(', ')}${josa(names[names.length - 1], '을', '를')} 구했다!`, 4);
      this.audio.jingle(true);
      this.fx({ kind: 'text', floor: this.leader.floor, x: this.leader.x, y: this.leader.y - 30, dur: 2, text: '구출!' });
      saveGame(this);
    }
  }
  tryEscape(c: Character): void {
    const captured = [...this.chars.values()].filter((ch) => ch.state === 'captured' || ch.state === 'carried');
    if (captured.length) { this.say(`아직 ${captured.map((x) => x.def.name).join(', ')}${josa(captured[captured.length - 1].def.name, '이', '가')} 갇혀 있다. 두고 갈 수는 없다.`, 4); this.audio.deny(); return; }
    const f = this.floorOf(c);
    const missing = [...this.chars.values()].filter((ch) => (ch.state === 'free' || ch.state === 'hidden' || ch.state === 'groggy') && ch.id !== c.id && !(ch.floor === 'ground' && roomAtPx(f, ch.x, ch.y)?.id === 'hall'));
    if (missing.length) { this.say(`${missing.map((x) => x.def.name).join(', ')}${josa(missing[missing.length - 1].def.name, '이', '가')} 아직 현관홀에 없다.`, 4); this.audio.deny(); return; }
    for (const ch of this.chars.values()) if (ch.state !== 'outside') ch.state = 'escaped';
    this.endStats = { time: this.time, runs: this.runs };
    this.phase = 'victory';
    this.phaseTimer = 0;
    this.audio.jingle(true);
    clearSave();
  }

  // ---------------- items ----------------
  /** add to a character's pockets; false when there is no room */
  addItem(c: Character, kind: ItemKind, count: number, text?: string): boolean {
    if (isPaper(kind)) { rememberPaper(this, kind, text); return true; }
    const def = ITEMS[kind];
    const existing = c.inventory.find((i) => i.kind === kind && def.stack);
    if (existing) { existing.count += count; return true; }
    if (c.inventory.length >= this.slotCount(c)) return false;
    c.inventory.push({ kind, count, text });
    return true;
  }

  pickup(c: Character, it: GroundItem): void {
    if (it.bag) {
      const contents = it.contents ?? [];
      // take the lot when it fits, otherwise open the bag so the player can choose and swap
      if (slotsNeeded(c.inventory, contents) <= this.slotCount(c) - c.inventory.length) {
        for (const inv of contents) this.addItem(c, inv.kind, inv.count, inv.text);
        this.items = this.items.filter((x) => x !== it);
        this.audio.pickup();
        this.say('가방을 챙겼다.', 2);
        return;
      }
      this.openBag(it);
      return;
    }
    if (!this.addItem(c, it.kind, it.count, it.text)) {
      // pockets are full: trade the selected item for this one
      const held = c.inventory[this.selectedSlot];
      if (!held) { this.say('주머니가 꽉 찼다.', 2); this.audio.deny(); return; }
      c.inventory[this.selectedSlot] = { kind: it.kind, count: it.count, text: it.text };
      it.kind = held.kind;
      it.count = held.count;
      it.text = held.text;
      this.audio.pickup();
      this.say(`${ITEMS[held.kind].name}${josa(ITEMS[held.kind].name, '을', '를')} 내려놓고 ${ITEMS[c.inventory[this.selectedSlot].kind].name}${josa(ITEMS[c.inventory[this.selectedSlot].kind].name, '을', '를')} 챙겼다.`, 3);
      return;
    }
    this.items = this.items.filter((x) => x !== it);
    this.audio.pickup();
    const def = ITEMS[it.kind];
    this.say(`${def.name}${josa(def.name, '을', '를')} 주웠다. ${def.desc}`, 3);
    if (def.use === 'read' && it.text) { this.noteText = it.text; this.overlay = 'note'; }
  }

  openBag(it: GroundItem): void {
    this.bagTarget = it;
    this.bagSide = 0;
    this.bagIndex = 0;
    this.bagNotice = null;
    this.overlay = 'bag';
    this.audio.ui();
  }

  /** C/Ctrl + G places an item carefully; a normal drop makes material-dependent noise. */
  dropSelected(): void {
    const c = this.leader;
    if (c.state !== 'free') return;
    const inv = c.inventory[this.selectedSlot];
    if (!inv) { this.say('내려놓을 것이 없다.', 1.5); this.audio.deny(); return; }
    c.inventory.splice(this.selectedSlot, 1);
    if (this.selectedSlot >= c.inventory.length) this.selectedSlot = Math.max(0, c.inventory.length - 1);
    const point = dropPoint(this.floorOf(c), c);
    this.items.push({ id: `drop:${inv.kind}:${this.time.toFixed(2)}`, kind: inv.kind, floor: c.floor, ...point, count: inv.count, text: inv.text });
    const careful = this.input.anyDown('ControlLeft', 'ControlRight', 'KeyC');
    const impact = dropSound(inv.kind, roomAtPx(this.floorOf(c), c.x, c.y)?.floor ?? 'wood', careful);
    this.emitNoise({ floor: c.floor, ...point, r: impact.radius, kind: 'impact', from: c.id });
    c.noiseLevel = impact.radius;
    this.audio.impact(impact.material, careful ? 0.12 : Math.min(1, impact.radius / 200));
    this.say(`${ITEMS[inv.kind].name}${josa(ITEMS[inv.kind].name, '을', '를')} ${careful ? '조심스럽게 내려놓았다.' : impact.radius >= 100 ? '떨어뜨렸다. 소리가 울려 퍼진다…' : '내려놓았다.'}`, 2.5);
  }
  removeItem(c: Character, kind: ItemKind, n: number): void {
    const inv = c.inventory.find((i) => i.kind === kind);
    if (!inv) return;
    inv.count -= n;
    if (inv.count <= 0) c.inventory = c.inventory.filter((i) => i !== inv);
    if (this.selectedSlot >= c.inventory.length) this.selectedSlot = Math.max(0, c.inventory.length - 1);
  }
  useSelected(): void {
    const c = this.leader;
    if (c.state !== 'free') return;
    const inv = c.inventory[this.selectedSlot];
    if (!inv) return;
    const def = ITEMS[inv.kind];
    switch (def.use) {
      case 'battery':
        if (c.battery >= 0.99) { this.say('배터리가 이미 가득하다.', 2); return; }
        c.battery = Math.min(1, c.battery + 0.5);
        this.removeItem(c, inv.kind, 1);
        this.audio.pickup();
        this.say('배터리를 갈아 끼웠다.', 2);
        return;
      case 'throw': {
        const { x: tx, y: ty } = this.aimTarget();
        let dx = tx - c.x, dy = ty - c.y;
        const d = Math.hypot(dx, dy) || 1;
        const range = Math.min(d, 140);
        dx /= d; dy /= d;
        const speed = range / 0.8;
        this.projectiles.push({ kind: inv.kind as 'bottle' | 'firecracker', floor: c.floor, x: c.x, y: c.y - 8, z: 8, vx: dx * speed, vy: dy * speed, vz: 60, fuse: 1.2, landed: false });
        this.removeItem(c, inv.kind, 1);
        c.facing = facingFromVec(dx, dy, c.facing);
        this.audio.ui();
        return;
      }
      case 'flash': {
        this.removeItem(c, 'camera', 1);
        this.fx({ kind: 'flash', floor: c.floor, x: c.x, y: this.eyeY(c), angle: c.flashAngle, r: 140, dur: 0.25 });
        this.audio.flash();
        const m = this.monster;
        if (m.floor === c.floor) {
          const d = dist(c.x, c.y, m.x, m.y);
          const a = Math.atan2(m.y - c.y, m.x - c.x);
          const f = this.floorOf(c);
          const visible = m.transit?.phase !== 'cross' && hasLos(c.x, this.eyeY(c), m.x, this.eyeY(m), this.occluders(f));
          if (visible && d < 100 && Math.abs(angleDiff(c.flashAngle, a)) < 0.7) {
            if (!this.stunMonster(3.5)) this.say('플래시에 괴물이 눈을 가리고 비틀거린다!', 3);
          } else if (visible && d < 300) {
            m.aware = 1; m.lastSeen = { floor: c.floor, x: c.x, y: c.y };
            if (m.state !== 'chase' && m.state !== 'carry') { m.state = 'investigate'; m.poi = { floor: c.floor, x: c.x, y: c.y }; m.path = null; }
            this.say('빗나갔다... 번쩍이는 빛에 괴물이 고개를 돌린다.', 3);
          } else this.say('찰칵.', 1.5);
        } else this.say('찰칵.', 1.5);
        return;
      }
      case 'read':
        if (isPaper(inv.kind)) rememberPaper(this, inv.kind, inv.text);
        this.noteText = inv.text ?? def.desc;
        this.overlay = 'note';
        return;
      default:
        this.say(def.key ? '열쇠는 맞는 문 앞에서 쓴다.' : inv.kind === 'crowbar' ? '판자로 막힌 문 앞에서 쓴다.' : inv.kind === 'fuse' ? '두꺼비집에 끼워야 한다.' : '지도는 M 키로 본다.', 2);
    }
  }

  /** Keyboard-only play aims forward; pointer input takes over when available. */
  aimTarget(): { x: number; y: number } {
    const c = this.leader;
    return this.input.mouseKnown && this.input.aimAngle === null
      ? { x: this.input.mouseX + this.camX, y: this.input.mouseY + this.camY }
      : { x: c.x + Math.cos(c.flashAngle) * 100, y: c.y + Math.sin(c.flashAngle) * 100 };
  }
  /** Tapping a portrait hands control to that friend. */
  takeControl(id: string): void {
    if (switchLeaderTo(this, id)) { this.audio.ui(); this.say(`${this.leader.def.name}${josa(this.leader.def.name, '을', '를')} 조종한다.`, 1.5); }
  }
  updateProjectiles(dt: number): void {
    for (const p of this.projectiles) {
      if (!p.landed) {
        const f = this.mansion.get(p.floor);
        const nx = p.x + p.vx * dt, ny = p.y + p.vy * dt;
        if (boxFree(f, nx, ny, (d) => d.open, 2, 2)) { p.x = nx; p.y = ny; } else { p.vx = 0; p.vy = 0; }
        p.vz -= 260 * dt;
        p.z += p.vz * dt;
        if (p.z <= 0) {
          p.z = 0; p.landed = true; p.vx = 0; p.vy = 0;
          if (p.kind === 'bottle') {
            this.emitNoise({ floor: p.floor, x: p.x, y: p.y, r: 240, kind: 'impact', from: 'thrown' });
            if (p.floor === this.leader.floor) this.audio.shatter();
            this.fx({ kind: 'sparkle', floor: p.floor, x: p.x, y: p.y, dur: 0.5 });
            p.fuse = -1;
          }
        }
      } else if (p.kind === 'firecracker') {
        p.fuse -= dt;
        if (p.fuse <= 0) {
          this.emitNoise({ floor: p.floor, x: p.x, y: p.y, r: 320, kind: 'bang', from: 'thrown' });
          if (p.floor === this.leader.floor) { this.audio.bang(); this.shake = 1; }
          this.fx({ kind: 'bang', floor: p.floor, x: p.x, y: p.y, dur: 0.5, r: 64 });
          const m = this.monster;
          if (m.floor === p.floor && m.transit?.phase !== 'cross' && dist(m.x, m.y, p.x, p.y) < 68 &&
              hasLos(p.x, p.y, m.x, this.eyeY(m), this.occluders(this.mansion.get(p.floor)))) {
            if (!this.stunMonster(4)) this.say('폭죽이 괴물 바로 옆에서 터졌다!', 3);
          }
          p.fuse = -1;
        }
      }
    }
    this.projectiles = this.projectiles.filter((p) => !(p.landed && p.fuse < 0));
  }
  /** Returns whether this hit freed carried friends, whose rescue message takes priority. */
  stunMonster(t: number): boolean {
    const m = this.monster;
    if (m.transit?.phase === 'cross') return false;
    m.transit = null; m.passagePlan = null; m.doorWait = null; m.passageCooldown = Math.max(m.passageCooldown, 15);
    m.state = 'stunned';
    m.stun = Math.max(m.stun, t);
    m.path = null;
    m.target = null;
    this.audio.growl(0.8);
    this.fx({ kind: 'stars', floor: m.floor, x: m.x, y: m.y - 30, dur: t });
    const released = m.carrying.length > 0;
    if (m.carrying.length) {
      const names = m.carrying.map((id) => this.chars.get(id)!.def.name);
      const f = this.mansion.get(m.floor);
      for (const id of m.carrying) {
        const c = this.chars.get(id)!;
        c.state = 'groggy';
        c.groggy = 1.2;
        c.mode = 'follow';
        const landing = { x: m.x, y: m.y };
        // The creature may have been interrupted while squeezing through a locked door.
        unstick(f, landing);
        moveBox(f, landing, this.rng.float(-12, 12), 10, d => d.open);
        c.x = landing.x;
        c.y = landing.y;
        c.floor = m.floor;
        c.path = null; c.pathTimer = 0; c.moving = false;
      }
      m.carrying = [];
      this.say(`괴물이 ${names.join(', ')}${josa(names[names.length - 1], '을', '를')} 놓쳤다!`, 3.5);
    }
    return released;
  }

  // ---------------- capture loop ----------------
  capture(t: Character): void {
    if (t.state === 'hidden') this.unhide(t);
    t.state = 'carried';
    t.flashOn = false;
    t.mode = 'follow';
    if (t.inventory.length) {
      this.items.push({ id: `bag:${t.id}:${this.items.length}`, kind: 'note', floor: t.floor, x: t.x, y: t.y, count: 1, bag: t.id, contents: t.inventory });
      t.inventory = [];
    }
    const m = this.monster;
    m.carrying.push(t.id);
    m.state = 'carry';
    m.target = null;
    m.path = null;
    this.captures++;
    this.audio.scream();
    this.shake = 1;
    this.say(`${t.def.name}${josa(t.def.name, '이', '가')} 잡혔다!`, 3);
    if (t.id === this.leaderId) {
      if (!switchLeader(this)) {
        this.phase = 'allCaptured';
        this.phaseTimer = 0;
      } else this.say(`${t.def.name}${josa(t.def.name, '이', '가')} 잡혔다! ${this.leader.def.name}${josa(this.leader.def.name, '으로', '로')} 바뀐다.`, 3);
    }
    saveGame(this);
  }
  swipe(c: Character): void {
    if (c.state !== 'free') return;
    c.state = 'groggy';
    c.groggy = 2.5 * (c.def.trait.groggyMul ?? 1);
    c.flashOn = false;
    const m = this.monster;
    const dx = c.x - m.x, dy = c.y - m.y;
    const d = Math.hypot(dx, dy) || 1;
    const f = this.floorOf(c);
    moveBox(f, c, (dx / d) * 18, (dy / d) * 18, () => false);
    this.audio.thud();
    this.shake = 0.6;
    this.fx({ kind: 'stars', floor: c.floor, x: c.x, y: c.y - 26, dur: c.groggy });
    if (c.id === this.leaderId) this.say('괴물의 팔에 맞았다! 머리가 핑 돈다...', 2.5);
  }
  activePrisonFor(m: Monster): Prison | null {
    void m;
    const captured = [...this.chars.values()].filter((c) => c.state === 'captured').length;
    const active = this.cfg.prisons.slice(0, captured >= 3 ? 2 : 1);
    let best: Prison | null = null, bn = 1e9;
    for (const p of active) {
      const n = [...this.chars.values()].filter((c) => c.state === 'captured' && c.prisonRoom === p.room).length;
      if (n < bn) { bn = n; best = p; }
    }
    return best;
  }
  imprisonChar(c: Character, prison: Prison): void {
    const f = this.mansion.get(prison.floor);
    const room = f.roomById.get(prison.room)!;
    c.state = 'captured';
    c.prisonRoom = prison.room;
    c.floor = prison.floor;
    c.mode = 'follow';
    for (let i = 0; i < 40; i++) {
      const tx = this.rng.int(room.x, room.x + room.w - 1), ty = this.rng.int(room.y, room.y + room.h - 1);
      if (f.solid[idx(f, tx, ty)] || f.tiles[idx(f, tx, ty)] !== T_FLOOR) continue;
      c.x = tileCenter(tx); c.y = tileCenter(ty);
      break;
    }
    const door = f.doors.find((d) => d.id === prison.doorId);
    if (door) { door.locked = true; door.open = false; }
  }
  imprison(m: Monster, prison: Prison): void {
    const names = m.carrying.map((id) => this.chars.get(id)!.def.name);
    for (const id of m.carrying) this.imprisonChar(this.chars.get(id)!, prison);
    m.carrying = [];
    const room = this.mansion.get(prison.floor).roomById.get(prison.room)!;
    const where = room.name.startsWith(FLOOR_NAMES[prison.floor]) ? room.name : `${FLOOR_NAMES[prison.floor]} ${room.name}`;
    this.say(`괴물이 ${names.join(', ')}${josa(names[names.length - 1], '을', '를')} ${where}에 가뒀다.`, 4);
    saveGame(this);
  }
  onMonsterSpotted(c: Character): void {
    if (c.id === this.leaderId) this.say('들켰다!', 1.5);
  }
  isLitByStatic(c: Character): boolean {
    const f = this.floorOf(c);
    const segs = this.occluders(f);
    for (const p of f.props) {
      const L = p.kind.light;
      if (!L || (L.powered && !this.flags.power)) continue;
      const [x, y] = propLightPos(p.kind, p.x, p.y);
      if (dist(x, y, c.x, c.y) >= L.r * 0.8) continue;
      const lightSegs = p.kind.tall ? withoutEmitter(segs, { x: p.x * TILE, y: p.y * TILE, w: p.kind.fw * TILE, h: p.kind.fh * TILE }) : segs;
      if (hasLos(x, y, c.x, this.eyeY(c), lightSegs)) return true;
    }
    for (const L of f.lights) {
      if (L.powered && !this.flags.power) continue;
      const boost = this.lightning > 0 && L.id?.startsWith('moon') ? 3.2 : 1;
      if (dist(L.x, L.y, c.x, c.y) >= L.r * boost * 0.7) continue;
      if (L.cone && Math.abs(angleDiff(L.cone.angle, Math.atan2(this.eyeY(c) - L.y, c.x - L.x))) > L.cone.half * (boost > 1 ? 1.3 : 1)) continue;
      if (hasLos(L.x, L.y, c.x, this.eyeY(c), segs)) return true;
    }
    return false;
  }

  updateDoors(dt: number): void {
    for (const f of this.mansion.floors.values()) for (const d of f.doors) if (d.anim > 0) d.anim -= dt;
  }
  updateEffects(dt: number): void {
    for (const e of this.effects) e.t += dt;
    this.effects = this.effects.filter((e) => e.t < e.dur);
    for (const n of this.noises) n.t -= dt;
    this.noises = this.noises.filter((n) => n.t > 0);
  }
  updateAmbience(dt: number): void {
    // lightning
    this.lightningTimer -= dt;
    if (this.lightningTimer <= 0) {
      this.lightning = 0.25;
      this.lightningTimer = this.rng.float(25, 70);
      this.thunderDelay = this.rng.float(0.6, 2.2);
    }
    if (this.lightning > 0) this.lightning -= dt;
    if (this.thunderDelay > 0) { this.thunderDelay -= dt; if (this.thunderDelay <= 0) this.audio.thunder(); }
    // heartbeat by monster proximity
    const c = this.leader;
    const m = this.monster;
    let level = 0;
    if (m.floor === c.floor && m.state !== 'lair' && passageOpacity(m) > 0) {
      const d = dist(c.x, c.y, m.x, m.y);
      const range = 240 * (c.def.trait.senseMul ?? 1);
      level = clamp(1 - d / range, 0, 1);
      if (m.state === 'chase' && m.target === c.id && d < range * 1.5) level = Math.max(level, 0.5);
      if (m.state === 'carry' && d < range * 0.65) level = Math.max(level, 0.6);
    }
    this.heart += (level - this.heart) * Math.min(1, dt * 3);
    this.audio.heartbeat(this.heart, dt);
  }

  // ---------------- overlays & screens ----------------
  updateOverlay(dt: number): void {
    const inp = this.input;
    void dt;
    if (this.overlay === 'journal') {
      if (inp.anyPressed('Escape', 'KeyJ')) { this.overlay = 'none'; return; }
      if (inp.pressed('KeyM')) { openMap(this); return; }
      const delta = inp.anyPressed('ArrowUp', 'KeyW') ? -1 : inp.anyPressed('ArrowDown', 'KeyS') ? 1 : Math.sign(inp.wheel);
      if (delta) { this.journalIndex = clamp(this.journalIndex + delta, 0, Math.max(0, this.journal.length - 1)); this.audio.ui(); }
      return;
    }
    if ((this.overlay === 'note' || this.overlay === 'map') && inp.pressed('KeyJ')) { this.overlay = 'journal'; return; }
    if (this.overlay === 'map') {
      if (inp.anyPressed('Escape', 'KeyE', 'Space', 'Enter', 'KeyM')) { this.overlay = 'none'; this.audio.ui(); return; }
      const delta = inp.anyPressed('ArrowLeft', 'KeyA') ? -1 : inp.anyPressed('ArrowRight', 'KeyD') ? 1 : Math.sign(inp.wheel);
      if (delta) changeMapFloor(this, delta);
      if (inp.mousePressed) clickMapFloor(this);
      return;
    }
    if (this.overlay === 'note' || this.overlay === 'help') {
      if (inp.anyPressed('Escape', 'KeyE', 'Space', 'Enter', 'KeyM') || inp.mousePressed) { this.overlay = 'none'; this.audio.ui(); }
      return;
    }
    if (this.overlay === 'bag') {
      const bag = this.bagTarget;
      const c = this.leader;
      if (!bag || !this.items.includes(bag)) { this.overlay = 'none'; this.bagTarget = null; return; }
      const list = () => (this.bagSide === 0 ? bag.contents ?? [] : c.inventory);
      if (inp.anyPressed('ArrowUp', 'KeyW')) { const n = list().length || 1; this.bagIndex = (this.bagIndex + n - 1) % n; this.audio.ui(); }
      if (inp.anyPressed('ArrowDown', 'KeyS')) { const n = list().length || 1; this.bagIndex = (this.bagIndex + 1) % n; this.audio.ui(); }
      if (inp.anyPressed('ArrowLeft', 'KeyA', 'ArrowRight', 'KeyD')) { this.bagSide = 1 - this.bagSide; this.bagIndex = 0; this.audio.ui(); }
      if (inp.anyPressed('Escape', 'KeyE')) { this.overlay = 'none'; this.bagTarget = null; return; }
      if (inp.anyPressed('Enter', 'Space')) this.moveBagItem(bag, c);
      return;
    }
    if (this.overlay === 'safe') {
      for (const ch of inp.typed) if (/[0-9]/.test(ch) && this.safeInput.length < 4) { this.safeInput += ch; this.audio.ui(); }
      if (inp.pressed('Backspace')) this.safeInput = this.safeInput.slice(0, -1);
      if (inp.pressed('Escape')) { this.overlay = 'none'; return; }
      if (inp.pressed('Enter') && this.safeInput.length === 4) {
        if (this.safeInput === this.cfg.safeCode && this.safeProp) {
          this.safeProp.data.opened = true;
          this.overlay = 'none';
          this.audio.unlock();
          const p = this.safeProp;
          const f = this.mansion.get(this.leader.floor);
          const spawn: Array<[ItemKind, number]> = [['key_third', 1], ['battery', 2], ['firecracker', 1]];
          spawn.forEach(([kind, count], i) => {
            this.items.push({ id: `safe:${kind}`, kind, floor: f.id, x: (p.x + 0.5) * TILE + (i - 1) * 12, y: (p.y + p.kind.fh) * TILE + 8, count });
          });
          this.say('금고가 열렸다! 안에서 열쇠와 물건들이 쏟아져 나왔다.', 4);
        } else {
          this.safeInput = '';
          this.audio.deny();
          this.say('틀렸다. 다이얼이 헛돈다.', 2);
        }
      }
      return;
    }
    if (this.overlay === 'pause') {
      const items = 3;
      if (inp.anyPressed('ArrowUp', 'KeyW')) { this.menuIndex = (this.menuIndex + items - 1) % items; this.audio.ui(); }
      if (inp.anyPressed('ArrowDown', 'KeyS')) { this.menuIndex = (this.menuIndex + 1) % items; this.audio.ui(); }
      if (inp.pressed('Escape')) { this.overlay = 'none'; return; }
      if (inp.anyPressed('Enter', 'Space')) {
        if (this.menuIndex === 0) this.overlay = 'none';
        else if (this.menuIndex === 1) this.overlay = 'help';
        else { saveGame(this); location.reload(); }
      }
    }
  }
  /** Move the highlighted item between the bag on the floor and the character's pockets. */
  moveBagItem(bag: GroundItem, c: Character): void {
    this.bagNotice = null;
    const contents = bag.contents ?? (bag.contents = []);
    if (this.bagSide === 0) {
      const inv = contents[this.bagIndex];
      if (!inv) return;
      if (!this.addItem(c, inv.kind, inv.count, inv.text)) {
        this.bagNotice = '주머니가 꽉 찼다. 오른쪽 소지품에서 가방에 넣을 물건을 골라라.';
        this.audio.deny();
        return;
      }
      contents.splice(this.bagIndex, 1);
      this.audio.pickup();
      if (contents.length === 0) {
        this.items = this.items.filter((x) => x !== bag);
        this.overlay = 'none';
        this.bagTarget = null;
        this.say('가방을 비웠다.', 2);
        return;
      }
      this.bagIndex = Math.min(this.bagIndex, contents.length - 1);
      return;
    }
    const inv = c.inventory[this.bagIndex];
    if (!inv) return;
    const same = contents.find((i) => i.kind === inv.kind && ITEMS[i.kind].stack);
    if (same) same.count += inv.count;
    else contents.push({ ...inv });
    c.inventory.splice(this.bagIndex, 1);
    if (this.selectedSlot >= c.inventory.length) this.selectedSlot = Math.max(0, c.inventory.length - 1);
    this.bagIndex = Math.max(0, Math.min(this.bagIndex, c.inventory.length - 1));
    this.audio.ui();
  }

  updateScreens(dt: number): void {
    const inp = this.input;
    void dt;
    switch (this.phase) {
      case 'title': {
        // thunder a beat after each lightning flash on the title scene
        const flash = titleFlashIndex(this.phaseTimer);
        if (flash !== this.titleFlash) { this.titleFlash = flash; if (flash > 0) this.thunderDelay = 0.7; }
        if (this.thunderDelay > 0) { this.thunderDelay -= dt; if (this.thunderDelay <= 0) this.audio.thunder(); }
        const items = this.hasSave ? 3 : 2;
        if (inp.anyPressed('ArrowUp', 'KeyW')) { this.menuIndex = (this.menuIndex + items - 1) % items; this.audio.ui(); }
        if (inp.anyPressed('ArrowDown', 'KeyS')) { this.menuIndex = (this.menuIndex + 1) % items; this.audio.ui(); }
        if (inp.anyPressed('Enter', 'Space') || inp.mousePressed) {
          this.audio.ui();
          const idxs = this.hasSave ? ['continue', 'new', 'help'] : ['new', 'help'];
          const pick = idxs[this.menuIndex];
          if (pick === 'new') this.startNewRun();
          else if (pick === 'continue') this.continueRun();
          else { this.phase = 'help'; }
        }
        break;
      }
      case 'help':
        if (inp.anyKeyPressed || inp.mousePressed) this.phase = this.entered.length ? 'play' : 'title';
        break;
      case 'select': {
        const cands = this.selectable();
        if (!cands.length) { this.phase = 'gameover'; this.phaseTimer = 0; break; }
        if (inp.anyPressed('ArrowLeft', 'KeyA')) { this.selectIndex = (this.selectIndex + cands.length - 1) % cands.length; this.audio.ui(); }
        if (inp.anyPressed('ArrowRight', 'KeyD')) { this.selectIndex = (this.selectIndex + 1) % cands.length; this.audio.ui(); }
        if (inp.anyPressed('Enter', 'Space', 'KeyE')) { this.audio.ui(); this.enterFriend(cands[this.selectIndex % cands.length].id); }
        break;
      }
      case 'intro':
        if (this.phaseTimer > 0.8 && (inp.anyKeyPressed || inp.mousePressed || this.phaseTimer > 6)) { this.phase = 'play'; saveGame(this); }
        break;
      case 'allCaptured':
        if (this.phaseTimer > 3.5) { this.phase = 'select'; this.selectIndex = 0; }
        break;
      case 'gameover':
        if (this.phaseTimer > 2 && (inp.anyKeyPressed || inp.mousePressed)) { clearSave(); location.reload(); }
        break;
      case 'victory':
        if (this.phaseTimer > 3 && (inp.anyKeyPressed || inp.mousePressed)) { clearSave(); location.reload(); }
        break;
    }
  }
  selectable(): FriendDef[] {
    return FRIENDS.filter((f) => this.chars.get(f.id)!.state === 'outside');
  }

  // ---------------- render ----------------
  render(alpha = 1): void {
    this.renderAlpha = this.phase === 'play' && this.overlay === 'none' ? alpha : 1;
    const now = performance.now();
    this.frameDt = this.lastRender ? Math.min(0.1, (now - this.lastRender) / 1000) : 1 / 60;
    this.lastRender = now;
    const { ctx, uictx } = this.screen;
    uictx.clearRect(0, 0, UI_W, UI_H);
    if (this.phase === 'title' || this.phase === 'help' && !this.entered.length) {
      ctx.fillStyle = '#05040a';
      ctx.fillRect(0, 0, INTERNAL_W, INTERNAL_H);
      drawScreens(this, ctx, uictx);
      return;
    }
    // pick whose eyes we are looking through; before anyone has entered, watch the captive instead
    let c = this.phase === 'allCaptured' ? this.chars.get(this.monster.carrying[0] ?? this.leaderId)! : this.leader;
    if (c.state === 'outside') c = [...this.chars.values()].find((x) => x.state === 'captured') ?? c;
    const f = this.floorOf(c);
    if (this.phase === 'play' && this.overlay === 'none') this.advanceCharacterMotions();
    const focusPos = this.visualPosition(c);
    let [cx, cy] = clampCamera(f, focusPos.x, focusPos.y - 8);
    if (this.shake > 0) { cx += Math.round((Math.random() - 0.5) * 6 * this.shake); cy += Math.round((Math.random() - 0.5) * 6 * this.shake); }
    this.camX = cx; this.camY = cy;
    const v = this.view;
    v.beginFrame(f, cx, cy);
    this.drawDynamic(f, c);
    if (this.debugOverview) {
      ctx.fillStyle = '#000';
      ctx.fillRect(0, 0, INTERNAL_W, INTERNAL_H);
      const sc = Math.min(INTERNAL_W / (f.w * TILE), INTERNAL_H / (f.h * TILE));
      drawFurnished(ctx, f, { x: 0, y: 0, w: f.w * TILE, h: f.h * TILE }, 0, 0, f.w * TILE * sc, f.h * TILE * sc);
      ctx.fillStyle = '#ff4040';
      ctx.fillRect(c.x * sc - 2, c.y * sc - 2, 4, 4);
      if (this.monster.floor === f.id) { ctx.fillStyle = '#ffd23a'; ctx.fillRect(this.monster.x * sc - 2, this.monster.y * sc - 2, 4, 4); }
      this.drawHud(uictx);
      return;
    }
    if (this.debugBright) drawRaster(ctx, this.view.world, 0, 0);
    else {
      this.drawLights(f, c);
      v.compose(ctx, f, 'rgba(12,10,30,0.78)');
    }
    this.drawLeaderVisibility(ctx, f);
    this.drawAfterLight(ctx, f);
    this.drawOverlays(ctx, f);
    this.drawHud(uictx);
    drawScreens(this, ctx, uictx);
  }

  drawDynamic(f: Floor, viewer: Character = this.leader): void {
    const w = this.view.wctx;
    const { camX, camY } = this;
    const walls = this.reachSegs(f);
    const canSee = (x: number, y: number, width = 2, height = 2) => this.debugBright ||
      visibleFootprint(viewer.x, this.eyeY(viewer), { x: x - width / 2, y: y - height / 2, w: width, h: height }, walls);
    this.visibleProps.clear();
    // Doors, furniture, and actors share the same depth order below.
    // ground items
    for (const it of this.items) {
      if (it.floor !== f.id || !canSee(it.x, it.y)) continue;
      const bob = Math.sin(this.time * 4 + it.x) * 1.5;
      const x = Math.round(it.x - 8 - camX), y = Math.round(it.y - 12 - camY + bob);
      w.fillStyle = 'rgba(0,0,0,0.3)';
      w.fillRect(x + 4, Math.round(it.y - 2 - camY), 8, 2);
      if (it.bag) drawRaster(w, propSprite(itemBagKind()), x, y - 2);
      else drawRaster(w, itemIcon(it.kind), x, y);
    }
    // projectiles
    for (const p of this.projectiles) {
      if (p.floor !== f.id || !canSee(p.x, p.y)) continue;
      const x = Math.round(p.x - camX), y = Math.round(p.y - camY);
      w.fillStyle = 'rgba(0,0,0,0.35)';
      w.fillRect(x - 2, y - 1, 4, 2);
      drawRaster(w, itemIcon(p.kind), x - 8, y - 8 - Math.round(p.z));
      if (p.kind === 'firecracker' && p.landed && Math.floor(this.time * 12) % 2 === 0) { w.fillStyle = '#fff1a8'; w.fillRect(x, y - 10 - Math.round(p.z), 2, 2); }
    }
    type Drawable = { y: number; draw: () => void };
    const list: Drawable[] = [];
    const lead = this.leader;
    const leadRect = this.charRect(lead);
    const track = lead.floor === f.id && (lead.state === 'free' || lead.state === 'hidden' || lead.state === 'groggy');
    this.coveredLeader = lead.state === 'hidden';
    this.coverRects = [];
    for (const c of this.chars.values()) {
      if (c.floor !== f.id || !['free', 'groggy', 'captured'].includes(c.state)) continue;
      if (c.id !== viewer.id && !canSee(c.x, this.eyeY(c), 8, 4)) continue;
      list.push({ y: c.y, draw: () => this.drawChar(w, c) });
    }
    const m = this.monster;
    if (m.floor === f.id && passageOpacity(m) > 0 && canSee(m.x, this.eyeY(m), 10, 4)) list.push({ y: m.y, draw: () => this.drawMonster(w) });
    for (const p of f.props) {
      if (p.kind.flat) continue;
      const r = propScreenRect(p);
      if (r.x + r.w < camX || r.x > camX + INTERNAL_W || r.y + r.h < camY || r.y > camY + INTERNAL_H) continue;
      const bottom = (p.y + p.kind.fh) * TILE;
      const visible = canSee((p.x + p.kind.fw / 2) * TILE, (p.y + p.kind.fh / 2) * TILE, p.kind.fw * TILE, p.kind.fh * TILE);
      if (visible) this.visibleProps.add(p);
      const covered = visible && track && ((bottom > lead.y && overlaps(leadRect, r)) || lead.hideProp === p.id);
      const opacity = coverOpacity(this.propOpacity.get(p) ?? 1, covered, this.frameDt);
      this.propOpacity.set(p, opacity);
      if (covered) { this.coveredLeader = true; this.coverRects.push(r); }
      list.push({ y: bottom, draw: () => {
        const memory = this.view.rctx;
        memory.save(); memory.globalAlpha = opacity; drawProp(memory, p, camX, camY); memory.restore();
        if (!visible) return;
        w.save(); w.globalAlpha = opacity;
        drawProp(w, p, camX, camY);
        if (!p.kind.light?.powered || this.flags.power) {
          w.save();
          if (p.flip) { w.translate(r.x - camX + r.w, r.y - camY); w.scale(-1, 1); }
          else w.translate(r.x - camX, r.y - camY);
          animateFurniture(w, p.kind.id, 0, 0, this.time + p.x * 0.13);
          w.restore();
        }
        w.restore();
      } });
    }
    for (const d of f.doors) {
      const r = { x: d.x * TILE, y: d.y * TILE - 4, w: d.orient === 'h' ? d.w * TILE : TILE, h: 24 };
      const covered = track && (d.y + 1) * TILE > lead.y && overlaps(leadRect, r) && !(d.kind === 'secret' && !d.revealed);
      const opacity = coverOpacity(this.doorOpacity.get(d) ?? 1, covered, this.frameDt);
      this.doorOpacity.set(d, opacity);
      if (covered) { this.coveredLeader = true; this.coverRects.push(r); }
      list.push({ y: (d.y + 1) * TILE, draw: () => {
        const memory = this.view.rctx;
        memory.save(); memory.globalAlpha = opacity; drawDoorEnt(memory, d, camX, camY); memory.restore();
        w.save(); w.globalAlpha = opacity; drawDoorEnt(w, d, camX, camY); w.restore();
      } });
    }
    list.sort((a, b) => a.y - b.y);
    for (const item of list) item.draw();
    // Reintroduce just the wall face covering the player's head. The underlying ground
    // stays opaque: the cutaway cannot reveal an unexplored neighbouring room.
    if (track) {
      for (let ty = Math.max(0, Math.floor(leadRect.y / TILE)); ty <= Math.min(f.h - 1, Math.floor(lead.y / TILE)); ty++) {
        for (let tx = Math.max(0, Math.floor(leadRect.x / TILE)); tx <= Math.min(f.w - 1, Math.floor((leadRect.x + leadRect.w) / TILE)); tx++) {
          if (f.tiles[idx(f, tx, ty)] !== T_WALL) continue;
          const r = { x: tx * TILE, y: ty * TILE, w: TILE, h: TILE };
          if (!overlaps(leadRect, r)) continue;
          this.coveredLeader = true; this.coverRects.push(r);
          w.save();
          w.beginPath(); w.rect(leadRect.x - camX, leadRect.y - camY, leadRect.w, leadRect.h); w.clip();
          const depth = Math.min(leadRect.y + leadRect.h, r.y + TILE) - Math.max(leadRect.y, r.y);
          w.globalAlpha = clamp(0.75 - depth / 28, 0.28, 0.75);
          drawRaster(w, terrainCanvas(f), r.x, r.y, TILE, TILE, r.x - camX, r.y - camY, TILE, TILE);
          w.restore();
        }
      }
    }
  }

  private visualPosition(c: { x: number; y: number; floor: FloorId }): { x: number; y: number } {
    const prev = this.previousPositions.get(c);
    if (!prev || prev.floor !== c.floor || Math.hypot(prev.x - c.x, prev.y - c.y) > TILE) return c;
    return { x: prev.x + (c.x - prev.x) * this.renderAlpha, y: prev.y + (c.y - prev.y) * this.renderAlpha };
  }

  private charRect(c: Character): Rect {
    const pos = this.visualPosition(c);
    return { x: Math.round((pos.x - SPR_W / 2) * 2) / 2, y: Math.round((pos.y - SPR_H + 2) * 2) / 2, w: SPR_W, h: SPR_H };
  }

  private expressionFor(c: Character): Expression {
    if (c.state === 'carried' || (c.id === this.leaderId && this.heart > 0.64)) return 'afraid';
    if (c.state === 'groggy') return 'tired';
    if (c.state === 'captured') return 'worried';
    if (c.state === 'hidden' || c.gait === 'sneak') return 'focused';
    if (c.state === 'escaped' || this.phase === 'victory' || c.bubble?.text === '고마워!') return 'relieved';
    if (c.gait === 'run' && c.moving || c.id === this.leaderId && this.heart > 0.25) return 'worried';
    if (c.battery < 0.1 && c.flashOn) return 'tired';
    return 'neutral';
  }

  private advanceCharacterMotions(): void {
    for (const actor of this.chars.values()) {
      const pos = this.visualPosition(actor);
      let motion = this.characterMotions.get(actor);
      const distance = motion ? Math.hypot(pos.x - motion.x, pos.y - motion.y) : 0;
      if (!motion || motion.floor !== actor.floor || distance > TILE) {
        motion = { ...createMotion(actor.gait), ...pos, floor: actor.floor };
        this.characterMotions.set(actor, motion);
      }
      advanceMotion(motion, actor.state === 'free' && actor.moving && distance <= TILE ? distance : 0,
        actor.gait, this.frameDt);
      motion.x = pos.x; motion.y = pos.y; motion.floor = actor.floor;
    }
  }

  private spriteFor(c: Character): HTMLCanvasElement {
    const time = this.time + FRIENDS.indexOf(c.def) * 0.43;
    const expression = this.expressionFor(c);
    const motion = this.characterMotions.get(c);
    if (c.state === 'free' && motion && motion.strength > 0) {
      return c.sprites.frame(c.facing, motion.gait, Math.floor(motion.phase * WALK_FRAMES) % WALK_FRAMES,
        expression, time % 4.7 > 4.55, 0, motion.strength);
    }
    return characterFrame(c.sprites, c.facing, c.state === 'hidden' ? 'sneak' : c.gait,
      false, 0, time, expression);
  }

  /** Only the controlled friend receives this local locator; no enemies leak through walls. */
  private drawLeaderVisibility(ctx: CanvasRenderingContext2D, f: Floor): void {
    const c = this.leader;
    if (c.floor !== f.id || !['free', 'hidden', 'groggy'].includes(c.state) || this.phase !== 'play') return;
    const r = this.charRect(c), x = r.x - this.camX, y = r.y - this.camY;
    if (c.state === 'hidden') {
      const hiding = f.props.find(p => p.id === c.hideProp);
      if (hiding) {
        ctx.save(); ctx.globalAlpha = 0.24;
        drawProp(ctx, hiding, this.camX, this.camY);
        ctx.restore();
      }
    }
    if (this.coveredLeader) {
      const spr = this.spriteFor(c);
      let ghost = this.ghostCache.get(spr);
      if (!ghost) { ghost = silhouette(spr, C.visibility); this.ghostCache.set(spr, ghost); }
      ctx.save();
      if (c.state !== 'hidden') {
        ctx.beginPath();
        for (const cover of this.coverRects) ctx.rect(cover.x - this.camX, cover.y - this.camY, cover.w, cover.h);
        ctx.clip();
      }
      ctx.globalAlpha = c.state === 'hidden' ? 0.65 : 0.38;
      drawRaster(ctx, ghost, x - 0.5, y); drawRaster(ctx, ghost, x + 0.5, y);
      drawRaster(ctx, ghost, x, y - 0.5);
      ctx.globalAlpha = c.state === 'hidden' ? 0.78 : 0.72;
      drawRaster(ctx, spr, x, y);
      ctx.restore();
    }
    ctx.save();
    ctx.fillStyle = c.state === 'hidden' ? C.visibility : C.sheet;
    ctx.globalAlpha = 0.85;
    const sy = y + SPR_H + 1;
    ctx.fillRect(x + SPR_W / 2 - 5, sy, 3, 0.5);
    ctx.fillRect(x + SPR_W / 2 + 2, sy, 3, 0.5);
    if (c.state === 'hidden') {
      ctx.fillRect(x + SPR_W / 2 - 2, y - 3, 4, 0.5); ctx.fillRect(x + SPR_W / 2 - 1, y - 2.5, 2, 0.5);
    }
    ctx.restore();
  }

  drawChar(ctx: CanvasRenderingContext2D, c: Character): void {
    const spr = this.spriteFor(c);
    const pos = this.visualPosition(c);
    const x = Math.round((pos.x - SPR_W / 2 - this.camX) * 2) / 2, y = Math.round((pos.y - SPR_H + 2 - this.camY) * 2) / 2;
    ctx.fillStyle = 'rgba(0,0,0,0.35)';
    ctx.beginPath(); ctx.ellipse(x + SPR_W / 2, y + SPR_H - 1, 6, 2, 0, 0, Math.PI * 2); ctx.fill();
    drawRaster(ctx, spr, x, y);
    if (c.id === this.leaderId && this.phase === 'play') {
      // small marker above the controlled character
      ctx.fillStyle = '#ffe6b8';
      ctx.fillRect(x + SPR_W / 2 - 1, y - 5 + Math.round(Math.sin(this.time * 5)), 2, 2);
    }
  }
  private monsterLayers(): Array<{ sprite: HTMLCanvasElement; x: number; y: number }> {
    const m = this.monster;
    const moving = m.state !== 'lair' && m.state !== 'stunned' && m.wait <= 0;
    const frame = moving ? Math.floor(m.anim * 30) % MON_FRAMES : 0;
    const pos = this.visualPosition(m);
    const x = Math.round(pos.x - MON_W / 2 - this.camX), y = Math.round(pos.y - MON_H + 4 - this.camY);
    const layers = [{ sprite: this.monSprites.frames[m.facing][frame], x, y }];
    m.carrying.forEach((id, i) => {
      const c = this.chars.get(id)!;
      const sprite = rotated(characterFrame(c.sprites, 'down', 'walk', false, 0, this.time, 'afraid'), i === 0 ? 1 : 3);
      layers.push({ sprite, x: x + (i === 0 ? -8 : 14), y: y + 2 + i * 6 });
    });
    return layers;
  }
  drawMonster(ctx: CanvasRenderingContext2D): void {
    const layers = this.monsterLayers();
    ctx.save(); ctx.globalAlpha *= passageOpacity(this.monster);
    ctx.fillStyle = 'rgba(0,0,0,0.45)';
    ctx.fillRect(layers[0].x + 5, layers[0].y + MON_H - 3, 14, 3);
    for (const { sprite, x, y } of layers) drawRaster(ctx, sprite, x, y);
    ctx.restore();
  }

  drawLights(f: Floor, focus: Character): void {
    const v = this.view;
    const segs = this.occluders(f);
    // what the viewer can actually see: everything else is only lit where this reaches
    const los = focus.floor === f.id && focus.state !== 'captured'
      ? visibility(focus.x, this.eyeY(focus), 560, segs, undefined, this.losVis, {
          x0: this.camX - 32, y0: this.camY - 32, x1: this.camX + INTERNAL_W + 32, y1: this.camY + INTERNAL_H + 32,
        })
      : null;
    for (const c of this.chars.values()) {
      if (c.floor !== f.id || !(c.state === 'free' || c.state === 'groggy' || c.state === 'captured')) continue;
      const sightR = 30 * (c.def.trait.sightMul ?? 1) * (c.state === 'captured' ? 0.7 : 1);
      v.addLight({ x: c.x, y: this.eyeY(c), r: sightR, color: '#9aa4c8', intensity: 0.8, inner: 0.15 }, segs, c.id === focus.id && c.state !== 'captured', f, c.id === focus.id ? 'main' : 'clipped');
      if (c.flashOn) {
        const low = c.battery < 0.12;
        const flick = low ? 0.6 + 0.4 * Math.abs(Math.sin(this.time * 37)) : 1;
        let r = 170 * (c.def.trait.lightRangeMul ?? 1);
        if (c.battery < 0.25) r *= 0.7 + c.battery;
        v.addLight({ x: c.x, y: this.eyeY(c), r, color: '#ffe6b8', intensity: flick, arc: { center: c.flashAngle, half: 0.42 }, inner: 0.02 }, segs, c.id === focus.id, f, c.id === focus.id ? 'main' : 'clipped');
      }
    }
    for (const p of f.props) {
      const L = p.kind.light;
      if (!L) continue;
      if (L.powered && !this.flags.power) continue;
      const [x, y] = propLightPos(p.kind, p.x, p.y);
      if (x < this.camX - L.r || x > this.camX + INTERNAL_W + L.r || y < this.camY - L.r || y > this.camY + INTERNAL_H + L.r) continue;
      const fl = L.flicker ? 0.94 + 0.035 * Math.sin(this.time * 7.3 + x) + 0.025 * Math.sin(this.time * 11.7 + y) : 1;
      const lightSegs = p.kind.tall ? withoutEmitter(segs, { x: p.x * TILE, y: p.y * TILE, w: p.kind.fw * TILE, h: p.kind.fh * TILE }) : segs;
      v.addLight({ x, y, r: L.r, color: L.color, intensity: (L.intensity ?? 0.9) * fl, inner: 0.1 }, lightSegs, false, f, 'clipped');
    }
    for (const L of f.lights) {
      if (L.powered && !this.flags.power) continue;
      const boost = this.lightning > 0 && L.id?.startsWith('moon') ? 3.2 : 1;
      const r = L.r * boost;
      if (L.x < this.camX - r || L.x > this.camX + INTERNAL_W + r || L.y < this.camY - r || L.y > this.camY + INTERNAL_H + r) continue;
      v.addLight({ x: L.x, y: L.y, r, color: boost > 1 ? '#dfe6ff' : L.color, intensity: boost > 1 ? 1 : 0.4, arc: L.cone ? { center: L.cone.angle, half: L.cone.half * (boost > 1 ? 1.3 : 1) } : undefined, inner: 0.05 }, segs, boost > 1, f, 'clipped');
    }
    // Brief countermeasure light stays at the source, even if the friend moves or turns.
    for (const e of this.effects) {
      if (e.floor !== f.id) continue;
      if (e.kind === 'flash') {
        v.addLight({ x: e.x, y: e.y, r: e.r ?? 140, color: '#e8f2ff', intensity: 1 - e.t / e.dur,
          arc: { center: e.angle ?? 0, half: 0.7 }, inner: 0.04 }, segs, true, f, 'clipped');
        continue;
      }
      if (e.kind !== 'bang') continue;
      v.addLight({ x: e.x, y: e.y, r: (e.r ?? 60) * (1 - e.t / e.dur) + 20, color: '#fff1a8', intensity: 1, inner: 0.1 }, segs, false, f, 'clipped');
    }
    v.finishLights(los);
    // Sample the light field on the CPU (see View.sampleLight); each sample sits on the centre
    // of the lightmap pixel it stands for. Upright furniture samples the visible near edge of its
    // footprint, so a tall bookcase or hearth is lit all the way up its drawn front.
    const half = 0.5 / WORLD_SCALE;
    for (const p of f.props) {
      if (p.kind.flat || !this.visibleProps.has(p)) continue;
      const r = propScreenRect(p);
      if (r.x + r.w < this.camX || r.x > this.camX + INTERNAL_W || r.y + r.h < this.camY || r.y > this.camY + INTERNAL_H) continue;
      const x0 = p.x * TILE, y0 = p.y * TILE, x1 = x0 + p.kind.fw * TILE, y1 = y0 + p.kind.fh * TILE;
      const sx = clamp(focus.x, x0 - 2, x1 + 2), sy = clamp(this.eyeY(focus), y0 - 2, y1 + 2);
      const sampleTile = f.tiles[idx(f, Math.floor(sx / TILE), Math.floor(sy / TILE))];
      if (sampleTile === T_WALL || sampleTile === T_VOID || !hasLos(focus.x, this.eyeY(focus), sx, sy, segs)) continue;
      const ix = Math.round(sx - this.camX), iy = Math.round(sy - this.camY);
      if (ix < 0 || iy < 0 || ix >= INTERNAL_W || iy >= INTERNAL_H) continue;
      v.lightSurface(propImage(p), r.x - this.camX, r.y - this.camY, v.sampleLight(this.camX + ix + half, this.camY + iy + half));
    }
    // A closed door stops rays at its near boundary, but its visible leaf still
    // receives the light arriving there. Shade only that sprite, never the room beyond.
    for (const d of f.doors) {
      if (d.open || d.kind === 'secret' && !d.revealed) continue;
      const x0 = d.x * TILE, y0 = d.y * TILE;
      const x1 = x0 + (d.orient === 'h' ? d.w * TILE : TILE);
      const y1 = y0 + (d.orient === 'v' ? d.w * TILE : TILE);
      const sx = d.orient === 'v' ? (focus.x < x0 ? x0 - 2 : x1 + 2) : clamp(focus.x, x0 + 2, x1 - 2);
      const sy = d.orient === 'h' ? (focus.y < y0 ? y0 - 2 : y1 + 2) : clamp(this.eyeY(focus), y0 + 2, y1 - 2);
      const ix = Math.round(sx - this.camX), iy = Math.round(sy - this.camY);
      if (ix < 0 || iy < 0 || ix >= INTERNAL_W || iy >= INTERNAL_H) continue;
      if (!hasLos(focus.x, this.eyeY(focus), sx, sy, segs)) continue;
      v.lightSurface(doorSprite(d, false), x0 - this.camX, y0 - (d.orient === 'v' ? 4 : 0) - this.camY, v.sampleLight(this.camX + ix + half, this.camY + iy + half));
    }
    // A character occupies vertical space above the floor-plane light origin. Sample
    // light at their feet and lift it onto their sprite so their expression stays legible.
    for (const actor of this.chars.values()) {
      if (actor.floor !== f.id || !['free', 'groggy', 'captured'].includes(actor.state)) continue;
      const own = actor.id === focus.id;
      if (!own && !hasLos(focus.x, this.eyeY(focus), actor.x, this.eyeY(actor), segs)) continue;
      const sx = Math.round((actor.x - this.camX) * WORLD_SCALE), sy = Math.round((this.eyeY(actor) - this.camY) * WORLD_SCALE);
      if (sx < 0 || sy < 0 || sx >= INTERNAL_W * WORLD_SCALE || sy >= INTERNAL_H * WORLD_SCALE) continue;
      const rgba = v.sampleLight(this.camX + (sx + 0.5) / WORLD_SCALE, this.camY + (sy + 0.5) / WORLD_SCALE);
      if (own) {
        rgba[0] = Math.max(rgba[0], 205); rgba[1] = Math.max(rgba[1], 210); rgba[2] = Math.max(rgba[2], 225); rgba[3] = Math.max(rgba[3], 210);
      }
      const r = this.charRect(actor);
      v.lightSurface(this.spriteFor(actor), r.x - this.camX, r.y - this.camY, rgba);
    }
    // The monster and carried friends stand above the same floor-plane light field.
    // Lift only light that actually reaches visible feet; darkness never gains a minimum glow.
    const m = this.monster, opacity = passageOpacity(m);
    if (m.floor === f.id && opacity > 0 && hasLos(focus.x, this.eyeY(focus), m.x, this.eyeY(m), segs)) {
      const sx = Math.round((m.x - this.camX) * WORLD_SCALE), sy = Math.round((this.eyeY(m) - this.camY) * WORLD_SCALE);
      if (sx >= 0 && sy >= 0 && sx < INTERNAL_W * WORLD_SCALE && sy < INTERNAL_H * WORLD_SCALE) {
        const rgba = v.sampleLight(this.camX + (sx + 0.5) / WORLD_SCALE, this.camY + (sy + 0.5) / WORLD_SCALE); rgba[3] *= opacity;
        for (const { sprite, x, y } of this.monsterLayers()) v.lightSurface(sprite, x, y, rgba);
      }
    }
  }

  /** walls plus closed doors: what a character cannot reach an interaction through */
  reachSegs(f: Floor): Seg[] {
    const segs = f.wallSegs.slice();
    for (const d of f.doors) {
      if (d.open) continue;
      // a shut door blocks reaching, but you must still be able to touch the door itself
      if (d.orient === 'h') segs.push({ ax: d.x * TILE + 3, ay: (d.y + 0.5) * TILE, bx: (d.x + d.w) * TILE - 3, by: (d.y + 0.5) * TILE });
      else segs.push({ ax: (d.x + 0.5) * TILE, ay: d.y * TILE + 3, bx: (d.x + 0.5) * TILE, by: (d.y + d.w) * TILE - 3 });
    }
    return segs;
  }

  occluders(f: Floor): Seg[] {
    const segs = f.segs.slice();
    for (const d of f.doors) segs.push(...doorLightSegments(d));
    return segs;
  }

  /**
   * Height the light and sight rays start from. Kept inside the character's own floor tile so
   * standing against a wall never puts the origin on the far side of that wall's blocker.
   */
  eyeY(c: { y: number }): number {
    return Math.max(c.y - 8, Math.floor(c.y / TILE) * TILE + 2);
  }

  /** things drawn on top of the lit scene: monster eyes, effects, bubbles */
  drawAfterLight(ctx: CanvasRenderingContext2D, f: Floor): void {
    const m = this.monster;
    if (m.floor === f.id && monsterEyesVisible(this, this.leader) && m.state !== 'stunned') {
      const pos = this.visualPosition(m);
      const x = Math.round(pos.x - MON_W / 2 - this.camX), y = Math.round(pos.y - MON_H + 4 - this.camY);
      const moving = m.state !== 'lair' && m.wait <= 0;
      const bob = moving ? monsterBob(Math.floor(m.anim * 30) % MON_FRAMES) : 0;
      for (const [ex, eyeY] of this.monSprites.eyes[m.facing]) {
        const ey = eyeY + bob;
        const g = ctx.createRadialGradient(x + ex + 1, y + ey + 1, 0, x + ex + 1, y + ey + 1, 5);
        g.addColorStop(0, 'rgba(255,210,58,0.9)');
        g.addColorStop(1, 'rgba(255,210,58,0)');
        ctx.fillStyle = g;
        ctx.fillRect(x + ex - 4, y + ey - 4, 10, 10);
        ctx.fillStyle = '#fff6c8';
        ctx.fillRect(x + ex, y + ey, 1.5, 1);
      }
    }
    for (const e of this.effects) {
      if (e.floor !== f.id) continue;
      // Stun stars sit above the head; test visibility at their actor's feet,
      // so they remain readable without revealing a monster behind a wall.
      if (e.kind === 'stars' && !this.debugBright) {
        const viewer = this.view.viewerVis;
        if (!viewer || !pointInPolygon(e.x, this.eyeY({ y: e.y + 30 }), viewer.pts, viewer.n)) continue;
      }
      const x = Math.round(e.x - this.camX), y = Math.round(e.y - this.camY);
      const k = e.t / e.dur;
      switch (e.kind) {
        case 'ring': {
          ctx.strokeStyle = `rgba(200,200,255,${0.5 * (1 - k)})`;
          ctx.lineWidth = 1;
          ctx.beginPath();
          ctx.arc(x, y, (e.r ?? 60) * k, 0, Math.PI * 2);
          ctx.stroke();
          break;
        }
        case 'bang': {
          drawBlast(ctx, e, this.occluders(f), this.debugBright ? undefined : this.view.viewerVis, this.camX, this.camY);
          break;
        }
        case 'stars': {
          for (let i = 0; i < 3; i++) {
            const a = this.time * 6 + (i * Math.PI * 2) / 3;
            ctx.fillStyle = '#ffe6b8';
            ctx.fillRect(x + Math.round(Math.cos(a) * 8) - 1, y + Math.round(Math.sin(a) * 3) - 1, 2, 2);
          }
          break;
        }
        case 'sparkle': {
          ctx.fillStyle = `rgba(200,240,255,${1 - k})`;
          for (let i = 0; i < 5; i++) ctx.fillRect(x + Math.round(Math.cos(i * 1.3) * 10 * k), y + Math.round(Math.sin(i * 1.3) * 6 * k), 1, 1);
          break;
        }
        case 'dust': {
          ctx.fillStyle = `rgba(150,130,110,${0.6 * (1 - k)})`;
          for (let i = 0; i < 6; i++) ctx.fillRect(x - 10 + i * 4, y - Math.round(6 * k), 2, 2);
          break;
        }
        case 'text':
          break;
      }
    }
    // speech bubbles are text: drawn on the UI layer
  }

  drawOverlays(ctx: CanvasRenderingContext2D, f: Floor): void {
    void f;
    if (this.interaction && this.phase === 'play') {
      const x = Math.round(this.interaction.x - this.camX), y = Math.round(this.interaction.y - this.camY - 14 + Math.sin(this.time * 6) * 1.5);
      ctx.fillStyle = '#f4f0e6';
      ctx.fillRect(x - 1, y - 4, 3, 3);
      ctx.fillRect(x, y - 6, 1, 1);
    }
    // aim dot for throwables
    const inv = this.leader.inventory[this.selectedSlot];
    if (inv && ITEMS[inv.kind].use === 'throw' && this.phase === 'play' && this.overlay === 'none') {
      const target = this.aimTarget();
      ctx.strokeStyle = 'rgba(255,230,184,0.6)';
      ctx.strokeRect(Math.round(target.x - this.camX) - 3, Math.round(target.y - this.camY) - 3, 6, 6);
    }
    // vignette, pulsing red when the monster is near
    const g = ctx.createRadialGradient(INTERNAL_W / 2, INTERNAL_H / 2, INTERNAL_H * 0.45, INTERNAL_W / 2, INTERNAL_H / 2, INTERNAL_W * 0.7);
    g.addColorStop(0, 'rgba(0,0,0,0)');
    g.addColorStop(1, `rgba(${Math.round(70 * this.heart)},0,0,${0.5 + 0.18 * this.heart * (0.5 + 0.5 * Math.sin(this.time * 8))})`);
    ctx.fillStyle = g;
    ctx.fillRect(0, 0, INTERNAL_W, INTERNAL_H);
    if (this.lightning > 0) {
      ctx.fillStyle = `rgba(200,210,255,${this.lightning * 0.5})`;
      ctx.fillRect(0, 0, INTERNAL_W, INTERNAL_H);
    }
  }

  drawHud(ui: CanvasRenderingContext2D): void {
    if (this.phase !== 'play' && this.phase !== 'allCaptured') return;
    const c = this.leader;
    const f = this.floorOf(c);
    const room = roomAtPx(f, c.x, c.y);
    text(ui, locationLabel(f.id, room?.name ?? '???'), UI_W - 16, 12, '#d9d0b8', 'right');
    // friends
    let px = 12;
    for (const fr of FRIENDS) {
      const ch = this.chars.get(fr.id)!;
      if (ch.state === 'outside') continue;
      const isLeader = ch.id === this.leaderId;
      const col = ch.state === 'captured' || ch.state === 'carried' ? '#c8433d' : ch.state === 'hidden' ? '#3b6fc7' : ch.state === 'groggy' ? '#8a5bb8' : ch.mode === 'wait' ? '#8a8a95' : '#4f9d5a';
      panel(ui, px, 10, 60, 82, isLeader ? 'rgba(60,45,20,0.9)' : 'rgba(8,6,14,0.75)', isLeader ? '#ffe6b8' : col);
      drawRaster(ui, characterPortrait(ch.sprites, this.expressionFor(ch), this.time + FRIENDS.indexOf(fr) * 0.43), px + 6, 14, 48, 48);
      text(ui, fr.name, px + 30, 63, isLeader ? '#ffe6b8' : '#d9d0b8', 'center', true);
      const tag = ch.state === 'captured' ? '갇힘' : ch.state === 'carried' ? '잡힘!' : ch.state === 'hidden' ? '숨음' : ch.state === 'groggy' ? '어질' : isLeader ? '조종' : ch.mode === 'wait' ? '대기' : ch.mode === 'hide' ? '숨기' : '따름';
      text(ui, tag, px + 30, 96, col, 'center');
      px += 68;
    }
    // bottom-left: flashlight & noise
    panel(ui, 12, UI_H - 62, 250, 50);
    text(ui, '손전등', 22, UI_H - 54, c.flashOn ? '#ffe6b8' : '#8a8a95');
    bar(ui, 90, UI_H - 52, 160, 14, c.battery, c.battery < 0.2 ? '#c8433d' : '#e2b53c');
    text(ui, T.gaitName[c.gait], 22, UI_H - 34, '#b8b0a0');
    bar(ui, 90, UI_H - 32, 160, 14, clamp(c.noiseLevel / 150, 0, 1), '#8a5bb8');
    // inventory hotbar
    const slots = this.slotCount(c);
    const sw = 44, x0 = UI_W / 2 - (slots * sw) / 2;
    for (let i = 0; i < slots; i++) {
      const x = x0 + i * sw, y = UI_H - 60;
      const sel = i === this.selectedSlot;
      panel(ui, x, y, 40, 40, sel ? 'rgba(60,45,20,0.9)' : 'rgba(8,6,14,0.75)', sel ? '#ffe6b8' : '#4a4058');
      const inv = c.inventory[i];
      if (inv) {
        drawScaled(ui, itemIcon(inv.kind), x + 4, y + 4, 2);
        if (inv.count > 1) text(ui, String(inv.count), x + 36, y + 22, '#f4f0e6', 'right');
      }
    }
    const sel = c.inventory[this.selectedSlot];
    const touch = this.input.touch;
    if (sel) text(ui, `${ITEMS[sel.kind].name}  ${touch ? '' : ITEMS[sel.kind].use === 'throw' ? '[Space/클릭] 던지기' : ITEMS[sel.kind].use === 'flash' ? '[Space] 플래시' : ITEMS[sel.kind].use === 'battery' ? '[Space] 교체' : ITEMS[sel.kind].use === 'read' ? '[Space] 읽기' : ''}`, UI_W / 2, UI_H - 82, '#d9d0b8', 'center');
    // Controls have their own baseline below the inventory.
    if (!touch) text(ui, `Tab 교체  1 따라와  2 대기  3 숨어  G ${c.gait === 'sneak' ? '조심히 놓기' : '떨어뜨리기'}  M 지도  J 수첩`, UI_W / 2, UI_H - 18, '#8e879b', 'center');
    if (this.interaction) text(ui, touch ? this.interaction.label : `[E] ${this.interaction.label}`, UI_W / 2, UI_H - 110, '#f4f0e6', 'center');
    if (this.message) text(ui, this.message.text, UI_W / 2, UI_H - 140, '#ffe6b8', 'center');
    // speech bubbles & floating text
    for (const ch of this.chars.values()) {
      if (ch.floor !== f.id || !ch.bubble) continue;
      text(ui, ch.bubble.text, (ch.x - this.camX) * 2, (ch.y - this.camY - 34) * 2, '#f4f0e6', 'center');
    }
    for (const e of this.effects) {
      if (e.kind !== 'text' || e.floor !== f.id) continue;
      text(ui, e.text ?? '', (e.x - this.camX) * 2, (e.y - this.camY - e.t * 10) * 2, '#ffe6b8', 'center');
    }
    if (this.monster.floor === f.id && this.monster.state === 'lair') {
      text(ui, '...무언가 잠들어 있다...', UI_W / 2, 40, '#8a5bb8', 'center');
    }
  }
}

import { PROPS } from '@/world/props';
function itemBagKind() {
  return PROPS.sack;
}

function facingFromAngleSafe(a: number, prev: Facing): Facing {
  const c = Math.cos(a), s = Math.sin(a);
  if (Math.abs(c) < 0.01 && Math.abs(s) < 0.01) return prev;
  return facingFromVec(c, s, prev);
}

/** Korean particle helper: josa(name, '이', '가') */
export function josa(word: string, withBatchim: string, without: string): string {
  const ch = word.charCodeAt(word.length - 1);
  if (ch < 0xac00 || ch > 0xd7a3) return without;
  const hasBatchim = (ch - 0xac00) % 28 !== 0;
  if (withBatchim === '으로') return hasBatchim && (ch - 0xac00) % 28 !== 8 ? withBatchim : without;
  return hasBatchim ? withBatchim : without;
}

function hasSaveFor(): boolean {
  try {
    return !!localStorage.getItem('homehomehome.save.v1');
  } catch {
    return false;
  }
}
